import React, { useState, useEffect, useCallback, useMemo, useRef, useTransition } from 'react'; // [FLUID-6 2026-10-12] useRef · [FLUID-8] useTransition
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import styled, { useTheme } from 'styled-components';
import { lgControlBg, lgCardSheen, lgTile } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import {
  fetchProcessAnalysis, peekCache, ProcessAnalysisData,
  PaKpiCard, PaTrendPoint, PaInventoryTrendPoint, PaIndustryRow,
  PaStateRow, PaPmQuadrantRow, PaClientBasePoint, PaMonthProgress,
  PaRevenueMixPoint,
  PaMoneyFlowPoint,
  PaMoneyComparePoint,
} from '../services/api';
import { SkeletonBox, Reveal } from '../components/Skeleton';
import { useRowsLand, useLandWindow, fluidCascade } from '../components/Fluid'; // [FLUID-6 2026-10-12]
import { LoadFailed } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import DrawnPath from '../components/DrawnPath'; // [MOTION-C 2026-08-20]
import GlassSelect from '../components/GlassSelect';
import TipGlass from '../components/TipGlass';
import { useIsMobile } from '../hooks/useIsMobile';
import { useRealtime } from '../hooks/useRealtime';
import { duration, easing, transition, reducedMotion } from '../styles/motion';
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width

/**
 * Process Analysis — the executive read of the business, for 2026.
 *
 * Written for someone opening it for the FIRST time. Every block states, in plain
 * English, what it is measuring and over what window; anything derived from partial
 * data prints its own coverage rather than implying completeness; and where a figure
 * could be confused with a similarly-named one elsewhere in the app (there are three
 * different "In Bank"s) the card says which one it is. If a number here needs a
 * verbal explanation to be understood, that is a bug in this file.
 *
 * All charts are hand-rolled SVG in the same visual language as the PM Performance
 * trend chart — the app ships no chart library and this tab does not add one.
 */

/* ── formatting ───────────────────────────────────────────────────────────────── */

const fmtMoney = (n: number) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const fmtMoneyShort = (n: number) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${v < 0 ? '-' : ''}$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${v < 0 ? '-' : ''}$${Math.round(a / 1_000)}k`;
  return `${v < 0 ? '-' : ''}$${Math.round(a)}`;
};
const fmtPct = (n: number, dp = 1) => `${(Number(n) || 0).toFixed(dp)}%`;
const fmtInt = (n: number) => (Number(n) || 0).toLocaleString('en-US');
const fmtNum1 = (n: number) => (Number(n) || 0).toFixed(1);

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-07' -> 'Jul' (the tab is single-year, so the year would be noise on every axis). */
const fmtMonth = (ym: string) => MONTHS_SHORT[Number((ym || '').split('-')[1]) - 1] || ym;
/* [2026-08-31, user] The month-comparison captions read the FULL month name. */
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtMonthYear = (ym: string) => `${fmtMonth(ym)} ${(ym || '').split('-')[0]}`;
/* [FULL NAMES 2026-08-31, user] The MONTH dropdown spells months out ("July 2026"); everywhere
   space is tight (pills, tooltips, axes) stays short. */
const fmtMonthYearFull = (ym: string) =>
  `${MONTHS_FULL[Number((ym || '').split('-')[1]) - 1] || ym} ${(ym || '').split('-')[0]}`;

/**
 * [MONTH CLARITY 2026-08-15] 'Jan–Jun 2026' from a month list, for the range pills beside a
 * card title. The Sales-team cards each cover a DIFFERENT window (the cohort runs to two
 * months short of today; the quartiles are the Scoreboard's last six COMPLETED months), and
 * neither matches the month picker on the KPI strip. That was only stated in the footnote
 * under the tables, so the figures got read as "this month's" — hence the pill.
 *
 * The year is carried once for a same-year range and on BOTH ends when the range crosses a
 * year, so a Nov 2026–Feb 2027 window can never read as a single year.
 */
const monthRangeLabel = (months: string[]): string => {
  if (!months.length) return '';
  const first = months[0];
  const last = months[months.length - 1];
  if (first === last) return fmtMonthYear(first);
  return first.slice(0, 4) === last.slice(0, 4)
    ? `${fmtMonth(first)}–${fmtMonthYear(last)}`
    : `${fmtMonthYear(first)}–${fmtMonthYear(last)}`;
};


/* ── palette ──────────────────────────────────────────────────────────────────── */

const GREEN = '#1a7f64';
const TEAL = '#3eafb3';
const AMBER = '#c98a1b';
const RED = '#c0392b';

/** Ink that stays legible on both themes, for SVG text where styled-components can't reach. */
function useInk() {
  const theme = useTheme() as any;
  const dark = theme.colorScheme === 'dark';
  return {
    dark,
    /* [FULL-INK 2026-09-01, user] `axis` is now for LINE STROKES ONLY — every text element
       that used it as a fill was moved to `strong`, and `label`/`strong`/`tipLabel` were
       raised to full ink (pure white on dark, slate-black on light). Do not put `axis` back
       on a text fill; the user has asked three times for no dim text on this tab. */
    axis: dark ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.5)',
    label: dark ? '#ffffff' : '#161917',
    strong: dark ? '#ffffff' : '#161917',
    grid: dark ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.08)',
    /* [LIGHT-MODE BARS 2026-08-17] Light was 0.18 alpha against dark mode's 0.42 — on white that
       read as a pale wash rather than a bar. Raised to 0.40 so the two themes carry comparable
       weight. It stays below the teal line's full strength on purpose: the line is the series
       drawn ON TOP of these bars and has to stay the more prominent of the two. */
    barSoft: dark ? 'rgba(14,126,78,0.42)' : 'rgba(20,107,84,0.40)',
    /* [MONEY FLOW 2026-08-27] The outflow bar below the axis. Red already means "lost" on this
       page (ClientBaseChart's cancellations), so refunds and lost disputes take the same hue at
       the same soft weight as `barSoft` — a bar, not a warning light. Full-strength RED is kept
       for its label so the figure stays legible over the card at 9px. */
    outflow: dark ? 'rgba(192,57,43,0.50)' : 'rgba(192,57,43,0.42)',
    /* [GREEN-ON-DARK 2026-08-27] Green as TEXT — chart labels, tooltip values, inline figures.
       The theme token (see theme.ts) rather than GREEN, which is a fill colour and sits near
       2:1 on the dark surface. Bars keep GREEN; anything that has to be READ takes this. */
    green: theme.greenInk as string,
    /* [GRAPHITE-EMERALD 2026-09-28] The bar/segment FILL green (was the fixed GREEN in both themes):
       jade #1a7f64 in light, emerald #0e7e4e in dark to match the dark theme's green600. */
    greenFill: dark ? '#0e7e4e' : GREEN,
    /* [SECOND-MONTH 2026-08-27] The paler, OPAQUE red for a subdivision inside a solid-RED bar.
       `outflow` cannot do this job — it is translucent, and over solid RED it disappears. Amber
       is off-limits (month-to-date) and so is a new hue: this is still a loss, just a younger
       one, so it stays in the red family a clear lightness step apart. */
    redSoft: dark ? '#e0705f' : '#e08b7d',
    tipBg: dark ? '#181818' : '#ffffff',
    tipBorder: dark ? 'rgba(255,255,255,0.22)' : 'rgba(22,25,23,0.16)',
    /* [TOOLTIP TEXT 2026-08-17] Tooltip LABELS used `axis`, which is half-transparent slate —
       fine over the card where the axis text sits, but these sit on a solid WHITE tooltip and
       read as pale grey. Solid slate here instead. Deliberately separate from `axis` so the
       on-chart axis labels keep their quieter tone. */
    tipLabel: dark ? '#ffffff' : '#161917',
    /* [REVENUE MIX 2026-08-17] The three stacked revenue buckets, as a set rather than three
       loose constants — in a stack the segments TOUCH, so what matters is that each pair stays
       apart, not that each looks nice alone.

       Green and teal are the page's own values, unchanged. That pairing is not habit: their
       separation comes from the LIGHTNESS gap between them, and every attempt to "improve" the
       teal by darkening it for light mode collapsed the pair (ΔE 16.6 → 8.3, i.e. hard to tell
       apart even with full colour vision). They stay exactly as the neighbouring charts draw
       them, which also keeps green meaning the same thing everywhere on this page.

       The third bucket needs a hue of its OWN: amber already means "month to date" on every
       chart here and red already means "lost", so reusing either would make the legend lie.

       [MONEY FLOW 2026-08-27, user] It WAS violet (#9182e4 dark / #6d5bd0 light), chosen for
       separation. The user rejected it on sight: "the top colour is not matching any section of
       the application". It is now the app's own accent, mint #1fd8a4 — the colour of the status
       dots, DataTable highlights, modals and logs — same value in both themes, as the app uses
       it. The stack reads as one green family, brightest on top: deep green retention → teal
       upsell → mint new sales. Mint and teal sit closer than violet and teal did (about ten
       steps of lightness and twenty degrees of hue apart), which is why the three readings below
       matter more, not less.

       ⚠ Teal on white, and green on slate, both land just under a 3:1 fill contrast. That is
       covered by the legend, the total printed over every column and the hover tooltip — three
       readings of the same figure that never depend on telling two fills apart. Remove those and
       the chart stops being legible, so they are not decoration. */
    /* [PIA IN GROSS 2026-09-01, user] A fourth bucket, so a fourth fill. It continues the same
       green ladder one clear lightness step above mint (emerald-200), which keeps the stack one
       family reading brightest-on-top and leaves amber ("month to date") and red ("lost")
       untouched. It is the smallest bucket by far — $400–$8,500 against a $220k+ column — so it
       lands as the cap, where a thin band is easiest to see, and the legend plus the tooltip
       carry the figure that a 1px sliver cannot. */
    mix: {
      retention: dark ? '#0e7e4e' : GREEN,
      upsell: TEAL,
      newSales: dark ? '#4acd8b' : '#1fd8a4',
      pia: dark ? '#aef2c9' : '#a2f3d7',
      shopify: dark ? '#b5d77a' : '#95bf47', // [SHOPIFY 2026-10-19] Shopify's own green
    },
  };
}

// [GLASS TOOLTIP 2026-08-31] Shared with TeamPerformancePage — see components/TipGlass.

/* ── layout ───────────────────────────────────────────────────────────────────── */

/* PageCard (AppLayout) is the scroll container and supplies 88px of bottom padding,
   which clears the floating dock (position:fixed, bottom:18px, ~64px tall → occupies
   the bottom ~82px) but NOT the "Go to Top" pill above it (position:fixed, bottom:96px,
   ~34px tall → protected zone reaches ~130px). This page ENDS on a card footnote rather
   than on a table row, so that last line was landing under the pill. The extra 60px here
   takes the total to ~148px and clears both.

   Mobile keeps the smaller value on purpose: GoToTop is hidden on phones (it stacked with
   the dock) and the dock itself sits at bottom:12px, so PageCard's 88px is already enough
   and more would just be dead space on a short screen. */
const Container = styled.div`
  background: ${(p) => p.theme.surface};
  padding: 14px 20px 60px;
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1);

  /* [PHONE-LAYOUT 2026-10-22] Bottom 8 -> 64 px. Measured on a 390 x 844 phone: PageCard's 88 px never lands after this
     page — AppLayout's PageFade wrapper is height:100%, the page overflows it, so the scroll ended flush with the card's
     bottom and the last Market row sat under the dock (dock top 769, card bottom 836). 64 px here (+ the last section's
     26 px margin) ends the page about 20 px above the dock. */
  @media (max-width: 768px) {
    padding: 12px 12px 64px;
  }
`;

const PageHead = styled.div`
  display: flex;
  /* [ONE-LINE HEAD 2026-08-31, user] middle-aligned on the row — title line and window pill
     share a vertical centre now that the head is a single line. */
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 14px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  margin-bottom: 20px;

  /* [PROCESS PHONE HEAD CENTRE 2026-09-23, user] "make this in middle … in phone". Phones: one
     centred column, the title line then the pill hint (see TitleLine / PillHint). Desktop keeps
     title left, hint right. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
  }
`;

/* [ONE-LINE HEAD 2026-08-31, user] The head is one line: "Process Analysis | January 2026
   onwards" — the long descriptive sentence is gone. Centre-aligned, NOT baseline: with a 20px
   title next to 12.5px text, baseline alignment leaves the small text optically low — the user
   flagged exactly that ("january onwards is looking a little down"). */
const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;

  /* [PROCESS PHONE HEAD CENTRE 2026-09-23, user] Phones: "Process Analysis | January 2026
     onwards" stays one line, centred (and wraps centred only on a phone too narrow for it). */
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: ${(p) => p.theme.text};
  margin: 0;
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the page title at the app's phone title size. */
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

/* [FULL-INK 2026-09-01, user] "for the complete process analysis tab, make the dim white text
   proper white in dark theme and black in light theme" — every prose/table text on this page
   that sat on theme.muted now reads this pair instead. Chart-internal furniture (axis month
   labels, gridlines, tooltip label column) is deliberately NOT swept: the muted gross label is
   what keeps the strong net label readable as the headline. */
const inkText = (p: any) => (p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917');

const TitleSep = styled.span`
  color: ${inkText};
  /* Punctuation, not information — full ink but kept translucent so the pipe stays a separator. */
  opacity: 0.55;
  font-size: 16px;
  font-weight: 300;
`;

const PageSub = styled.p`
  font-size: 12.5px;
  color: ${inkText};
  margin: 0;
  line-height: 1.5;
`;

/**
 * [PILL HINT 2026-09-01, user] The page head's right-hand line, and now its only right-hand
 * content: the "Jan – Sep 2026" WindowPill that sat beside it was removed, since every section
 * states its own period on its own head and the page-level pill only repeated them. PageHead is
 * space-between, so this sits right on its own — the HeadRight wrapper that held the pair went
 * with the pill.
 *
 * It exists because the amber pills are a CONTROL that reads as a label. Nothing said they could
 * be clicked, and four sections now carry one holding its own month — so a reader who does not
 * know that sees four inconsistent dates rather than four independent filters.
 *
 * In the page's ordinary ink, NOT amber: the line covers fixed periods as well as switchable
 * ones, so amber would claim the whole sentence was about the amber pills. `inkText` rather than
 * a literal white, which would vanish in light mode.
 *
 * Wide enough to hold the sentence on ONE line (~445px at 11.5px) — a two-line hint up here
 * reads as a paragraph rather than a caption.
 */
const PillHint = styled.p`
  font-size: 11.5px;
  line-height: 1.4;
  margin: 0;
  color: ${inkText};
  text-align: right;
  max-width: 620px;

  /* [PROCESS PHONE HEAD CENTRE 2026-09-23, user] Phones: the hint is centred under the title.
     [PHONE-LAYOUT 2026-10-22] and read at 12 px. */
  @media (max-width: 768px) {
    text-align: center;
    font-size: 12px;
  }
`;

/* [2026-09-01, user] WindowPill — the page-level "Jan – Sep 2026" badge — was DELETED here, not
   just unmounted: it had one call site and an unrendered styled component fails the CI build.
   `windowLabel` itself is still very much in use, as the RangePill on the Revenue engine,
   Geography and Market heads. Recover the pill from commit d9e0bca if it is ever wanted back. */

const Section = styled.section<{ $land?: boolean }>`
  margin-bottom: 26px;
  /* [FLUID-6 2026-10-12, user] "process analysis is not having the animation": when a section's data lands (a tab
     visit, after the skeleton; or this section's own month pick) what sits under its head — the cards with their
     charts and tables — rises in, upper block first, on the page blocks' rhythm (40 ms in, 35 ms apart; see
     components/Fluid.tsx). The head never moves. Only while $land (useLandWindow), so the live refresh never replays it. */
  ${(p) => (p.$land ? fluidCascade('> :not(:first-child)', 0, 20, 3) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const SectionHead = styled.div`
  margin-bottom: 12px;
`;

/* [ONE-LINE HEAD 2026-09-01, user] SectionHeadRow, SectionHeadNote and SectionTitleRow were
   DELETED here, not merely unused: every section head now runs on one row (see OneLineHead), and
   a styled component nobody renders fails the CI build's no-unused-vars.

   They were the two-row arrangement built earlier the same day — eyebrow above, then a
   space-between row of "title left, explaining line right", baseline-aligned. If this test is
   reverted, take them back from commit d9e0bca along with the eight section heads that used
   them; the headline's own block is also saved verbatim in the session scratchpad. */

const Eyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: ${(p) => p.theme.greenInk};
  margin-bottom: 3px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px, the smallest label size on a phone. */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 15.5px;
  font-weight: 750;
  color: ${(p) => p.theme.text};
  margin: 0 0 3px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a touch larger, and it wraps (see OneLineHead). */
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.3;
  }
`;

/* [OWN PILLS 2026-08-31, user] The big MONTH dropdown (MonthSelect, 2026-08-17) is GONE — the
   amber PILL on each month-scoped section title is now itself the picker (PillSelect below), one
   per section, each independent. The amber contract survives the change: amber still means "this
   follows a chosen month", it just lives on the control itself now. */
const SectionNote = styled.p`
  font-size: 12px;
  color: ${inkText};
  margin: 0;
  line-height: 1.5;
  max-width: 860px;
`;

/* [ONE-LINE HEAD 2026-09-01, user] SectionHeadNote lived here — the right-hand explaining line
   of the two-row head. Removed with SectionHeadRow and SectionTitleRow when every section moved
   to one row; OneLineNote below replaces it. Recover from commit d9e0bca if the test is reverted. */

/**
 * [ONE-LINE HEAD 2026-09-01, user — TEST, may be reverted] A section's whole head on a single
 * row: eyebrow | title | measure line | month filter, divided by the page's own TitleSep pipe
 * (the same separator the page title uses, so the idiom is not new). Used by the headline and
 * the sales-process sections; the rest keep the two-row arrangement for now.
 *
 * Colours and wording are untouched — only the arrangement changes. Baseline-aligned so four
 * different type sizes sit on one line rather than on their own tops.
 *
 * The measure line takes the free space and stays LEFT-aligned here: it is mid-row now, not the
 * right-hand column SectionHeadNote was built for, and right-aligning a middle item would open a
 * gap before the pipe. Wraps as a whole rather than mid-sentence once the row runs out.
 */
const OneLineHead = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;

  ${Eyebrow} {
    margin-bottom: 0;
    flex-shrink: 0;
  }
  ${SectionTitle} {
    margin: 0;
    flex-shrink: 0;
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: one row cannot hold eyebrow | title | note | pill, and the title (which never
     shrank) pushed the whole page card 38 px sideways. Three rows instead, no pipes: the eyebrow with the month / range
     pill at the right end, then the title (wrapping), then the note. OneLineNote and OneLineRight set their own order.
     The eyebrow row is always 36 px tall (the month pill's tap height), so a pill that arrives with the data never
     moves the head, and every section head keeps the same rhythm. */
  @media (max-width: 768px) {
    align-items: center;
    gap: 6px 10px;

    ${TitleSep} {
      display: none;
    }
    ${Eyebrow} {
      order: 1;
      min-height: 36px;
      display: flex;
      align-items: center;
    }
    ${SectionTitle} {
      order: 3;
      flex: 1 1 100%;
      min-width: 0;
    }
  }
`;

const OneLineNote = styled(SectionNote)`
  flex: 1 1 260px;
  min-width: 0;
  text-align: left;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the note's own row under the title (see OneLineHead). */
  @media (max-width: 768px) {
    order: 4;
    flex: 1 1 100%;
    font-size: 12.5px;
  }
`;

/**
 * [FAR RIGHT 2026-09-01, user] Holds the month filter hard against the row's right edge, with no
 * pipe before it — "remove the line before the month filter and make the month filter to the far
 * right side".
 *
 * `margin-left: auto` rather than growing the note, because OneLineNote inherits SectionNote's
 * 860px cap: past that it stops growing and the slack collected at the end of the row instead,
 * which is what left the pill stranded mid-row. The auto margin eats that slack wherever it is.
 *
 * The pill is a control, not a phrase, so it does not take a separator — the gap now does that
 * job, and it reads as an action parked at the end rather than the last item in a sentence.
 */
const OneLineRight = styled.span`
  margin-left: auto;
  flex-shrink: 0;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the pill shares the eyebrow's row, at its right end (see OneLineHead). */
  @media (max-width: 768px) {
    order: 2;
  }
`;

const Grid = styled.div<{ $cols: number; $land?: boolean }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, minmax(0, 1fr));
  gap: 14px;

  ${layoutMax(1180)} {
    grid-template-columns: repeat(${(p) => Math.min(p.$cols, 2)}, minmax(0, 1fr));
  }
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px; /* [PHONE-LAYOUT 2026-10-22] phone spacing */
  }
  /* [FLUID-6 2026-10-12, user] The headline's KPI tiles rise in one after another when their figures land or the
     headline month changes — 18 ms apart, as tiles do on a view switch (components/Fluid.tsx). Only while $land. */
  ${(p) => (p.$land ? fluidCascade('> *', 0, 18, 4) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const Card = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
  background: ${(p) => p.theme.surface};
  padding: 14px 16px 16px;
  min-width: 0;
  transition: border-color 180ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${(p) => p.theme.borderHover};
    box-shadow: ${(p) => p.theme.shadow};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: 12 px inside, so tables and charts get the width. */
  @media (max-width: 768px) {
    padding: 12px 12px 14px;
  }
`;

const CardTitle = styled.h3`
  font-size: 13.5px;
  font-weight: 750;
  color: ${(p) => p.theme.text};
  margin: 0 0 2px;

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 14.5px;
  }
`;

/* [MONTH CLARITY 2026-08-15] Title + the window the card actually covers, on one line.
   Wraps rather than truncates: the range is the point, so it must never be the thing that
   gets clipped on a narrow column. */
const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin: 0 0 2px;

  ${CardTitle} {
    margin: 0;
  }
`;

/* The date range beside a card title. Deliberately quiet — it must read as a qualifier on
   the heading, not compete with it. */
const RangePill = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  white-space: nowrap;
  color: ${inkText};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)')};
  border: 1px solid ${(p) => p.theme.line};

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11.5 px (labels never below 11 px on a phone). */
  @media (max-width: 768px) {
    padding: 3px 9px;
    font-size: 11.5px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'transparent' : 'rgba(22,25,23,0.12)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : '#ffffff')}, 0 1px 2px rgba(0,0,0,0.08);
`;

/* [PILL FILTER 2026-08-31, user] The amber month pill on a section title is a WORKING month
   filter — "someone can click this and select the month here". The headline, Sales process and
   Retention pipeline each wear one, EACH HOLDING ITS OWN month; the central MONTH dropdown they
   used to follow is gone. Amber keeps its 2026-08-17 meaning — "this value follows a chosen
   month", as opposed to the grey RangePills' fixed ranges — the control just lives on the pill
   itself now. GlassSelect gives the glass list, keyboard nav and type-ahead for free; the
   overrides below shrink its trigger to the old LivePill's exact footprint (2×8 padding, 10.5px,
   999px radius, amber, flat — no sheen, no blur) so at rest it is indistinguishable from the
   static pills beside other card titles, except for the small chevron that says it opens. */
const PillSelect = styled(GlassSelect)`
  && {
    padding: 2px 20px 2px 8px;
    border-radius: 999px;
    min-width: 0;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.2px;
    color: ${AMBER};
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(201,138,27,0.55)' : 'rgba(201,138,27,0.45)')};
    background-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(201,138,27,0.12)' : 'rgba(201,138,27,0.09)')};
    background-image: none;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  && span { color: ${AMBER}; }
  /* The chevron span — scaled down to pill proportions. */
  && > span:last-child {
    right: 6px;
    width: 9px;
    height: 9px;
    margin-top: -4.5px;
  }
  && svg { width: 9px; height: 9px; }
  &&:hover {
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(201,138,27,0.90)' : 'rgba(201,138,27,0.75)')};
    background-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(201,138,27,0.18)' : 'rgba(201,138,27,0.14)')};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: the month pill is a real tap target, 36 px tall with 12.5 px text and a
     full-size arrow; still the amber pill, still at the right end of its section's eyebrow row. */
  @media (max-width: 768px) {
    && {
      min-height: 36px;
      padding: 7px 30px 7px 13px;
      font-size: 12.5px;
    }
    && > span:last-child {
      right: 11px;
      width: 12px;
      height: 12px;
      margin-top: -6px;
    }
    && svg { width: 12px; height: 12px; }
  }
`;

/* The one-line plain-English explainer under every card title. This is what makes the
   tab readable by someone who has never seen it — it is not decoration, do not drop it. */
const CardSub = styled.p`
  font-size: 11.5px;
  color: ${inkText};
  margin: 0 0 12px;
  line-height: 1.45;

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 12.5px;
  }
`;

const CardFoot = styled.p`
  font-size: 10.5px;
  color: ${inkText};
  margin: 10px 0 0;
  line-height: 1.45;
  opacity: 0.9;

  /* [PHONE-LAYOUT 2026-10-22] Phones: captions and summaries at 12 px. */
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

/**
 * [ARROW LEGEND 2026-09-01, user] Title left, a one-line explainer right — the section heads'
 * arrangement, one level down. The compare charts print a ▲/▼ beside every year on the x-axis and
 * nothing on the card said what it measured.
 *
 * ⚠ An earlier attempt at this line was REMOVED the same day ("it is not explaining the true
 * meaning") — that was while the figure still sat on the cluster label and compared the last two
 * complete years of the whole cluster. Now that it sits on each YEAR and compares that bar with
 * the one beside it, the sentence below is the honest description. If the figure ever moves
 * again, this copy moves with it.
 */
const CardHeadRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;

  ${CardTitle} {
    margin: 0 0 2px;
  }
`;

const CardHeadNote = styled.span`
  flex: 0 1 auto;
  max-width: 640px;
  font-size: 11px;
  line-height: 1.45;
  color: ${inkText};
  text-align: right;

  ${layoutMax(1024)} {
    flex: 1 1 100%;
    max-width: 100%;
    text-align: left;
  }
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

/* [TREND SUMMARY 2026-09-01, user] The computed two-line summaries sit centred under their
   legends ("make the summary lines to be in the middle") — the legend above them is centred
   too, so left-aligned text under it read as misaligned. Only the summaries: ordinary
   footnotes stay left-aligned prose. */
const SummaryFoot = styled(CardFoot)`
  text-align: center;
`;

const Empty = styled.div`
  padding: 26px 8px;
  text-align: center;
  font-size: 12px;
  color: ${inkText};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ErrorBox = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${(p) => p.theme.danger};
  font-size: 13px;
  font-weight: 600;
`;

/* ── KPI card ─────────────────────────────────────────────────────────────────── */

const KpiCardBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${inkText};
`;

const KpiValue = styled.div`
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.8px;
  color: ${(p) => p.theme.text};
  line-height: 1.15;
  margin-top: 4px;
`;

const KpiRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const Delta = styled.span<{ $dir: 'up' | 'down' | 'flat' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 750;
  padding: 2px 7px;
  border-radius: 999px;
  color: ${(p) => (p.$dir === 'up' ? p.theme.greenInk : p.$dir === 'down' ? RED : inkText(p))};
  background: ${(p) => (p.$dir === 'up'
    ? p.theme.greenTint
    : p.$dir === 'down' ? 'rgba(192,57,43,0.12)' : 'transparent')};
  /* [DELTA TOOLTIP 2026-09-01, user] The pill carries a title explaining the comparison.
     ⛔ NOT cursor: help — that draws the question-mark cursor, which the user removed on sight.
     Plain arrow: it beats the I-beam a bare span would otherwise show over text. */
  cursor: default;

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const DeltaNote = styled.span`
  font-size: 11px;
  /* [2026-09-01, user] Was theme.muted and read as dim beside the delta pill — full ink now:
     white on dark, the app's slate-black on light (same pair SheetTitle uses). */
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917')};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

/* [2026-09-01, user] The KPI cards' one-line captions get the same full ink — they are the
   cards' explanation, not a footnote. Other CardFoot uses stay muted. */
const KpiCaption = styled(CardFoot)`
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917')};
  opacity: 1;
`;

/** Which month the headline figure is. Stated because it is NOT today's month. */
/* [MONTH PICKER 2026-08-17] Amber, matching the picker in the section heading. These three pills
   and that dropdown are the same fact — the month the headline is reporting on — and in grey they
   read as static labels rather than as the thing the control changes. Amber already means "the
   month in question" here (it is the month-to-date marker), so this is the existing meaning
   applied consistently rather than a new colour. */
/* [DELTA IN THE HEAD 2026-09-01, user] KpiMonth — the amber "Aug 2026" tag that used to sit in
   each card's top-right corner — was REMOVED, not just unmounted: it had one call site, and a
   styled component nobody renders is a lint failure in the CI build. The change pill now holds
   that corner. If the tag is ever wanted back, it was a 10.5px amber pill (AMBER text, 0.12/0.09
   amber fill, 0.55/0.45 amber border, radius 999px, padding 1px 7px). */

const KpiHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

/* [PHONE-LAYOUT 2026-10-22] The headline's four KPI tiles. Desktop: the Grid exactly as before (this adds rules for
   phones only). Phones: a full-size swipe row instead of four tall tiles stacked over two screens — each tile 84 % of
   the width with the next one peeking, snapping tile by tile (the user's KPI-strip choice). The skeleton uses it too. */
const KpiSwipe = styled(Grid)`
  @media (max-width: 768px) {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
    padding-bottom: 4px;

    & > * {
      flex: 0 0 84%;
      min-width: 0;
      scroll-snap-align: start;
    }
  }
`;

/** What one KPI card reduces to once a month is chosen. */
interface KpiView {
  value: number;
  /** Mean of the COMPLETE months BEFORE the selected one. */
  priorAvg: number;
  /** How many months that average is over — stated, so a 1-month "average" isn't oversold. */
  priorCount: number;
  /** [2026-08-27, user] The months behind the average, named — "vs $X monthly average from
   *  Jan – Jun 2026" reads as a fact; "of the 6 earlier months" made the reader count. */
  priorFrom: string | null;
  priorTo: string | null;
  /** null whenever a percentage would be dishonest: a part month, or a zero baseline. */
  deltaPct: number | null;
  /** Set when the selected month is the one still running. */
  partial: PaMonthProgress | null;
}

/**
 * Reduce a KPI card to one month. This is the ONLY place the headline arithmetic lives —
 * the server ships per-month facts and nothing else, precisely so there cannot be a second
 * copy of this formula free to drift from it.
 *
 * The baseline is the complete months BEFORE the selected one, so "against the 2026
 * run-rate" means the same thing whichever month is picked: May reads against Jan–Apr, not
 * against its own future. Two cases get no percentage at all — a month still in progress (a
 * few days measured against whole months is the −100% reading this tab was built out of),
 * and a zero baseline, which is undefined rather than infinite growth.
 */
function kpiForMonth(card: PaKpiCard, month: string): KpiView {
  const partial = card.mtd && card.mtd.month === month ? card.mtd : null;
  const idx = card.series.findIndex((p) => p.month === month);
  const value = partial ? partial.value : idx >= 0 ? card.series[idx].value : 0;
  // For the running month every complete month in the window is an earlier one. `idx` of -1
  // (a month that is neither) slices to empty rather than to the whole series.
  const prior = partial ? card.series : card.series.slice(0, Math.max(0, idx));
  const priorAvg = prior.length ? prior.reduce((s, p) => s + p.value, 0) / prior.length : 0;
  return {
    value,
    priorAvg,
    priorCount: prior.length,
    priorFrom: prior.length ? prior[0].month : null,
    priorTo: prior.length ? prior[prior.length - 1].month : null,
    deltaPct: partial || priorAvg <= 0 ? null : ((value - priorAvg) / priorAvg) * 100,
    partial,
  };
}

/**
 * [DELTA TOOLTIP 2026-09-01, user] Hover copy for the green/red percentage pill.
 *
 * ⚠ ONE SHORT SENTENCE. The first cut ran to three and the user cut it back on sight ("make the
 * tooltip short, right now it is too big"). It also must not merely restate DeltaNote, which
 * already prints "vs 120.8 monthly average from Jan – Jul 2026" right beside the pill — what
 * the pill alone cannot say is which DIRECTION and against HOW MANY months, so that is all
 * this adds.
 *
 * Every branch kpiForMonth can produce has copy, the no-percentage ones included: a bare dash
 * is exactly what a reader wants explained. Keep these in step with that function — a string
 * here must never claim a comparison it refused to draw.
 */
function deltaTooltip(v: KpiView, month: string, format: (n: number) => string): string {
  const monthName = fmtMonthYear(month);
  if (v.partial) {
    return `${monthName} is still running — day ${v.partial.day} of ${v.partial.daysInMonth}. `
      + 'No percentage until it ends.';
  }
  if (v.priorCount === 0) return `No earlier month to compare ${monthName} with.`;
  if (v.deltaPct == null) return 'The earlier months average zero, so there is no percentage.';

  const d = v.deltaPct;
  const months = `${v.priorCount} month${v.priorCount === 1 ? '' : 's'} before it`;
  if (Math.abs(d) < 0.05) {
    return `${monthName} is level with the ${format(v.priorAvg)} average of the ${months}.`;
  }
  return `${monthName} is ${fmtPct(Math.abs(d), 1)} ${d > 0 ? 'higher' : 'lower'} than the `
    + `${format(v.priorAvg)} average of the ${months}.`;
}

/** Tiny trend line under a KPI — shape only, no axes; the numbers are stated above it. */
const Sparkline: React.FC<{
  points: { month: string; value: number }[];
  color: string;
  /** The selected month, so the dot marks what the card is reporting, not just the end. */
  highlight?: string;
}> = ({ points, color, highlight }) => {
  const ink = useInk();
  if (points.length < 2) return null;
  const W = 240, H = 34, pad = 3;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i: number) => pad + (i / (points.length - 1)) * (W - pad * 2);
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2);
  const line = points.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} ${x(points.length - 1).toFixed(1)},${H} ${x(0).toFixed(1)},${H}`;
  // Falls back to the end of the line when the selection isn't a complete month (i.e. the
  // running month is selected), which is the dot this had before it could be highlighted.
  const hi = highlight ? points.findIndex((p) => p.month === highlight) : -1;
  const mark = hi >= 0 ? hi : points.length - 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
      style={{ display: 'block', marginTop: 10, overflow: 'visible' }} aria-hidden="true">
      <polygon points={area} fill={color} opacity={ink.dark ? 0.16 : 0.1} />
      <polyline points={line} fill="none" stroke={color} strokeWidth={1.75}
        vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(mark)} cy={y(points[mark].value)} r={hi >= 0 ? 3.4 : 2.6} fill={color} />
    </svg>
  );
};

const KpiTile: React.FC<{
  label: string;
  sub: string;
  card: PaKpiCard;
  /** The month the whole headline section is reporting on. */
  month: string;
  format: (n: number) => string;
  color: string;
  /** true when a fall is the GOOD direction (nothing here yet, but churn-style KPIs would). */
  invert?: boolean;
}> = ({ label, sub, card, month, format, color, invert }) => {
  const v = kpiForMonth(card, month);
  const d = v.deltaPct;
  const dir: 'up' | 'down' | 'flat' = d == null || Math.abs(d) < 0.05
    ? 'flat'
    : (d > 0) !== !!invert ? 'up' : 'down';
  return (
    <KpiCardBox>
      {/* [DELTA IN THE HEAD 2026-09-01, user] The amber month tag is GONE from the card and the
          change pill takes its corner: the month is already stated once, on the section's own
          filter above, so repeating it on all four cards spent the most valuable corner of each
          restating a constant. The change is the one number that differs card to card.
          [DELTA TOOLTIP 2026-09-01, user] The title rides the PILL itself — the user pointed at
          the percentage chip. Plain title=, which the app-wide themed tooltip host turns into the
          glass capsule after its dwell. The dash branch gets copy too, since "why is there no
          number" is the better question. */}
      <KpiHead>
        <KpiLabel>{label}</KpiLabel>
        {d == null
          ? <Delta $dir="flat" title={deltaTooltip(v, month, format)}>—</Delta>
          : <Delta $dir={dir} title={deltaTooltip(v, month, format)}>
              {d > 0 ? '▲' : d < 0 ? '▼' : '■'} {fmtPct(Math.abs(d), 1)}
            </Delta>}
      </KpiHead>
      <KpiValue>{format(v.value)}</KpiValue>
      {/* With the pill lifted into the head, the note is the row's only child and so starts hard
          at the left edge — the "shift a little left" the user asked for, from the layout rather
          than from a margin. */}
      <KpiRow>
        <DeltaNote>
          {v.partial
            ? `Month to date — day ${v.partial.day} of ${v.partial.daysInMonth}, so no comparison is drawn`
            : v.priorCount === 0
              ? 'The first month of 2026 — nothing earlier to compare it against'
              : v.priorFrom === v.priorTo
                ? `vs ${format(v.priorAvg)} in ${fmtMonthYear(v.priorFrom!)}, the one earlier month`
                : `vs ${format(v.priorAvg)} monthly average from ${fmtMonth(v.priorFrom!)} – ${fmtMonthYear(v.priorTo!)}`}
        </DeltaNote>
      </KpiRow>
      <Sparkline points={card.series} color={color} highlight={month} />
      {/* [KPI-CAPTION 2026-08-31, user] One plain-English line under the graph and nothing
          else — the "Aug so far · day N of N" part-month strip was removed on request. */}
      <KpiCaption>{sub}</KpiCaption>
    </KpiCardBox>
  );
};

/* ── ranked bar list ──────────────────────────────────────────────────────────── */

const BarRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 6px 0;

  & + & {
    border-top: 1px solid ${(p) => p.theme.line};
  }
`;

const BarLabel = styled.div`
  min-width: 0;
`;

const BarName = styled.div`
  font-size: 12px;
  font-weight: 650;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 13 px, and a long industry name wraps instead of losing its end. */
  @media (max-width: 768px) {
    font-size: 13px;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    overflow-wrap: break-word;
  }
`;

const BarTrack = styled.div`
  height: 6px;
  border-radius: 4px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.06)')};
  margin-top: 5px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $w: number; $color: string }>`
  height: 100%;
  width: ${(p) => Math.max(1.5, p.$w)}%;
  background: ${(p) => p.$color};
  border-radius: 4px;
  transition: width 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const BarValue = styled.div`
  text-align: right;
  white-space: nowrap;
`;

const BarPrimary = styled.div`
  font-size: 12.5px;
  font-weight: 750;
  color: ${(p) => p.theme.text};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 13.5px;
  }
`;

const BarMeta = styled.div`
  font-size: 10.5px;
  color: ${inkText};
  margin-top: 2px;

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 11.5px;
  }
`;

interface BarItem { name: string; value: number; primary: string; meta: string }

const BarList: React.FC<{ items: BarItem[]; color: string }> = ({ items, color }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  if (!items.length) return <Empty>Not enough data yet.</Empty>;
  return (
    <div>
      {items.map((it) => (
        <BarRow key={it.name}>
          <BarLabel>
            {/* [2026-08-27, user] No title attr — see StateInd. The themed capsule fired on
                every bar row in the industry cards; the label ellipsizes instead. */}
            <BarName>{it.name}</BarName>
            <BarTrack><BarFill $w={(it.value / max) * 100} $color={color} /></BarTrack>
          </BarLabel>
          <BarValue>
            <BarPrimary>{it.primary}</BarPrimary>
            <BarMeta>{it.meta}</BarMeta>
          </BarValue>
        </BarRow>
      ))}
    </div>
  );
};

/* ── combo chart (bars + line), the app's established trend language ──────────── */

interface ComboPoint {
  month: string;
  bar: number;
  /**
   * [SECOND BAR 2026-08-17] Optional companion bar, drawn beside  rather than stacked on
   * it — the two are independent figures (money out vs money kept back), not parts of a whole,
   * and stacking would imply a total that means nothing.
   */
  bar2?: number;
  line: number;
  /**
   * Extra tooltip rows under the line value, already formatted. Lets a chart break its bar
   * down into the parts it is made of without adding a second series to the plot.
   */
  extra?: { label: string; value: string }[];
}

/**
 * [TREND SUMMARY 2026-09-01, user] Two computed sentences for under a combo chart's legend:
 * the window's general direction, then the last month held against the others — "a summary
 * based on the general thing for the data like growth or decrease and the emphasis on the
 * last month". COMPUTED, never written by hand, so it re-states itself every month.
 *
 * Complete months only: the running month is excluded from every term (on day 1 it would drag
 * the average down and read as a collapse — the same rule the headline cards follow). Under
 * three complete months there is no "trend" worth asserting, so nothing is returned and the
 * card simply has no summary rather than a fabricated one.
 */
function comboTrendLines(
  points: ComboPoint[],
  runningMonth: string | null,
  subject: string,
  /** grammatical number of the subject — 'New sales HAVE', 'Placed inventory HAS'. */
  plural: boolean,
  /** the verb the last month did: 'brought in' / 'placed'. */
  verb: string,
  fmt: (n: number) => string,
  /** one closing remark about the LINE series of the last complete month. */
  lineTail: (last: ComboPoint) => string,
): [string, string] | null {
  const complete = points.filter((p) => p.month !== runningMonth);
  if (complete.length < 3) return null;

  const last = complete[complete.length - 1];
  const earlier = complete.slice(0, -1);
  const prev = earlier[earlier.length - 1];
  const mean = (xs: ComboPoint[]) => xs.reduce((s, p) => s + p.bar, 0) / xs.length;
  const avgAll = mean(complete);
  const avgEarlier = mean(earlier);
  const peak = complete.reduce((a, b) => (b.bar > a.bar ? b : a));

  /* Direction = first half of the window against the second. ±5% of noise-band, because
     calling two flat halves "growth" is how a dashboard loses the reader's trust. */
  const half = Math.floor(complete.length / 2);
  const a1 = mean(complete.slice(0, half));
  const a2 = mean(complete.slice(half));
  const trendPct = a1 > 0 ? ((a2 - a1) / a1) * 100 : 0;
  const dir = trendPct > 5
    ? (plural ? 'have been climbing' : 'has been climbing')
    : trendPct < -5
      ? (plural ? 'have been easing' : 'has been easing')
      : (plural ? 'have held steady' : 'has held steady');

  const peakClause = peak.month === last.month
    ? `and the latest month is the best yet at ${fmt(peak.bar)}`
    : `with ${fmtMonthYear(peak.month)} the best month at ${fmt(peak.bar)}`;
  const line1 = `${subject} ${dir} — ${fmt(avgAll)} a month on average, ${peakClause}.`;

  const vsAvg = avgEarlier > 0 ? ((last.bar - avgEarlier) / avgEarlier) * 100 : null;
  const vsPrev = prev.bar > 0 ? ((last.bar - prev.bar) / prev.bar) * 100 : null;
  const avgClause = vsAvg == null ? ''
    : Math.abs(vsAvg) < 1 ? ' — level with the earlier months’ average'
      : ` — ${Math.abs(vsAvg).toFixed(0)}% ${vsAvg > 0 ? 'above' : 'below'} the earlier months’ average`;
  const prevClause = vsPrev == null ? ''
    : Math.abs(vsPrev) < 1 ? ` and level with ${fmtMonth(prev.month)}`
      : ` and ${Math.abs(vsPrev).toFixed(0)}% ${vsPrev > 0 ? 'up' : 'down'} on ${fmtMonth(prev.month)}`;
  const line2 = `${fmtMonthYear(last.month)} ${verb} ${fmt(last.bar)}${avgClause}${prevClause}. ${lineTail(last)}`;

  return [line1, line2];
}

const ChartScroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the chart swipes inside its own box without dragging the page along. */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

/**
 * [PHONE-LAYOUT 2026-10-22] How wide a chart draws on a phone. Every chart here is an SVG over a fixed viewBox, so its
 * text renders at (drawn width / viewBox width) x its font size: at the old 560 px floor the 9-unit labels came out at
 * 6.6 px, and the quarter / month comparison charts (1060 / 1520 units) at 3 to 5 px. On phones each chart now draws at
 * 1.2 x its viewBox width (9-unit labels = 10.8 px) and swipes sideways inside its card; the dense month chart, whose
 * smallest labels are 7.5 units, at 1.34 x. So the swipe stays short, the month charts take a narrower phone viewBox
 * (fewer units per month, still wider than their labels) and the quadrant scatter is drawn to fit the card instead.
 */
const PHONE_CHART_SCALE = 1.2;
const phoneChartWidth = (viewW: number, scale = PHONE_CHART_SCALE) => Math.round(viewW * scale);

/**
 * [SMOOTH LINE 2026-08-17] An SVG path through the points with rounded corners instead of the
 * hard elbows a `<polyline>` gives.
 *
 * ⚠ MONOTONE cubic (Fritsch–Carlson), deliberately NOT a plain Catmull–Rom or cardinal spline.
 * The easy smoothing curves OVERSHOOT: between two points they can bulge past both, so a
 * retention line that never exceeds 100% in the data can be DRAWN above 100%, and a trough can
 * be drawn below the real minimum. On a chart people read figures off, that is a lie rather than
 * a style. Monotone interpolation is constrained so the curve can never leave the range of the
 * two points it connects — every visible peak and trough is a real data point.
 *
 * Falls back to a straight segment for fewer than three points, where smoothing means nothing.
 */
function smoothPath(pts: { x: number; y: number }[]): string {
  const n = pts.length;
  if (n === 0) return '';
  if (n < 3) return pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  // Secant slopes between consecutive points.
  const dx: number[] = [], dy: number[] = [], slope: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    dx[i] = pts[i + 1].x - pts[i].x;
    dy[i] = pts[i + 1].y - pts[i].y;
    slope[i] = dx[i] === 0 ? 0 : dy[i] / dx[i];
  }

  // Tangents: average of neighbouring secants, one-sided at the ends.
  const m: number[] = new Array(n);
  m[0] = slope[0];
  m[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    m[i] = (slope[i - 1] * slope[i] <= 0) ? 0 : (slope[i - 1] + slope[i]) / 2;
  }

  // Fritsch–Carlson limiter — this is the step that removes overshoot.
  for (let i = 0; i < n - 1; i += 1) {
    if (slope[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * slope[i];
      m[i + 1] = t * b * slope[i];
    }
  }

  let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < n - 1; i += 1) {
    const h = dx[i] / 3;
    const c1x = pts[i].x + h, c1y = pts[i].y + m[i] * h;
    const c2x = pts[i + 1].x - h, c2y = pts[i + 1].y - m[i + 1] * h;
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)}`
      + ` ${pts[i + 1].x.toFixed(1)},${pts[i + 1].y.toFixed(1)}`;
  }
  return d;
}

const ComboChart: React.FC<{
  points: ComboPoint[];
  barLabel: string;
  lineLabel: string;
  /** Bar labels ON the chart — abbreviated, they have to fit above a 40px bar. */
  formatBar: (n: number) => string;
  /** Bar value in the TOOLTIP, where there is room for the exact figure. Defaults to formatBar. */
  formatBarFull?: (n: number) => string;
  formatLine: (n: number) => string;
  lineColor?: string;
  /** The month still running, if any — drawn as an outline so it never reads as a collapse. */
  progress?: PaMonthProgress | null;
  /**
   * Put the breakdown rows ABOVE the line value in the tooltip. Use when the line is derived
   * from an extra row (a percentage of it), so the tooltip reads in the order the figure is
   * built rather than plot-order.
   */
  extraFirst?: boolean;
  /** Legend/tooltip label for the optional second bar. Required when any point carries bar2. */
  bar2Label?: string;
  bar2Color?: string;
}> = ({
  points, barLabel, lineLabel, formatBar, formatBarFull, formatLine, lineColor = TEAL, progress,
  extraFirst, bar2Label, bar2Color,
}) => {
  const ink = useInk();
  const [hover, setHover] = useState<number | null>(null);
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22] the chart's phone width
  if (!points.length) return <Empty>No data for this period.</Empty>;
  // [MONTH-BOUNDARY 2026-08-03] A month that is three days old plotted as a solid bar next to
  // twelve finished ones reads as a cliff. It is drawn hollow and labelled instead.
  const partial = (i: number) => !!progress && points[i].month === progress.month;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 40 units per month instead of the desktop's ~73 (a bar label needs ~27), so the
     chart drawn at phone size (x 1.2, labels 10.8 px) shows seven or eight months at a time, not three. */
  const W = isMobile ? Math.max(300, 28 + points.length * 40) : 760, H = 260, ml = 14, mr = 14, mt = 30, mb = 34;
  const pw = W - ml - mr, ph = H - mt - mb;
  const n = Math.max(points.length, 1);
  const hasBar2 = points.some((p) => p.bar2 != null);
  // Shared scale: two bars measuring pounds must be readable against each other.
  const maxBar = Math.max(...points.map((p) => Math.max(p.bar, p.bar2 ?? 0)), 1);
  // Headroom above the line so its labels never collide with the top edge.
  const maxLine = Math.max(...points.map((p) => p.line), 1) * 1.3;
  const band = pw / n;
  const barW = hasBar2 ? Math.min(band * 0.24, 20) : Math.min(band * 0.5, 40);
  // Grouped: first bar sits left of centre, second right of it. Centred when there is only one.
  const barX = (i: number, second: boolean) =>
    (hasBar2 ? cx(i) + (second ? 1 : -barW - 1) : cx(i) - barW / 2);
  const cx = (i: number) => ml + band * i + band / 2;
  const yBar = (v: number) => mt + ph - (v / maxBar) * ph;
  const yLine = (v: number) => mt + ph - (v / maxLine) * ph;
  const line = smoothPath(points.map((p, i) => ({ x: cx(i), y: yLine(p.line) })));

  const hp = hover != null ? points[hover] : null;
  const extras = hp?.extra ?? [];
  // One flat, ordered list so the tooltip has a single layout rule regardless of how many
  // rows a chart contributes or where its breakdown belongs.
  const tipRows: { label: string; value: string; color: string; weight: number }[] = hp
    ? (() => {
      const bar = { label: barLabel, value: (formatBarFull ?? formatBar)(hp.bar), color: ink.strong, weight: 700 };
      const line = { label: lineLabel, value: formatLine(hp.line), color: lineColor, weight: 750 };
      const ex = extras.map((e) => ({ label: e.label, value: e.value, color: ink.strong, weight: 700 }));
      // The second bar reads directly under the first — they are the pair being compared.
      const bar2 = hp.bar2 != null
        ? [{ label: bar2Label ?? 'Second', value: (formatBarFull ?? formatBar)(hp.bar2), color: bar2Color ?? TEAL, weight: 700 }]
        : [];
      return extraFirst ? [bar, ...bar2, ...ex, line] : [bar, ...bar2, line, ...ex];
    })()
    : [];
  // The tooltip grows with what it has to say: a row per line, plus the month-to-date note
  // when the hovered month is the one still running.
  /* [TOOLTIP OVERFLOW 2026-08-17] tipW was a flat 150/172, which silently assumed every caller's
     labels were short. "Refunds and lost disputes: $11,749" needs ~155 units and spilled out past
     the rounded box. SVG text cannot be measured without touching the DOM, so the width is
     estimated from the character count instead — ~4.5 units per glyph at fontSize 9, ~5.2 for the
     bolder 9.5 header — and the old fixed values become a FLOOR rather than a ceiling. Slightly
     generous on narrow strings, which costs nothing, and never clips a long one. */
  const tipTextW = Math.max(
    ...tipRows.map((r) => (r.label.length + 2 + r.value.length) * 4.5),
    hp ? fmtMonthYear(hp.month).length * 5.2 : 0,
    0,
  );
  const tipW = Math.max(extras.length ? 172 : 150, Math.ceil(tipTextW) + 20);
  const tipH = 26 + tipRows.length * 14 + (hover != null && partial(hover) ? 16 : 0);
  const tipX = hover != null ? Math.max(4, Math.min(W - tipW - 4, cx(hover) - tipW / 2)) : 0;
  const anchor = hp ? Math.min(yLine(hp.line), yBar(hp.bar)) : 0;
  const tipY = anchor - tipH - 12 < 4 ? anchor + 16 : anchor - tipH - 12;

  return (
    <ChartScroll>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? phoneChartWidth(W) : 560 /* [PHONE-LAYOUT 2026-10-22] */ }} onMouseLeave={() => setHover(null)}>
        {/* horizontal guides — three is enough to read level without becoming graph paper */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={ml} y1={mt + ph * f} x2={W - mr} y2={mt + ph * f}
            stroke={ink.grid} strokeWidth={1} />
        ))}
        <line x1={ml} y1={mt + ph} x2={W - mr} y2={mt + ph} stroke={ink.axis} strokeWidth={0.75} />

        {hover != null && (
          <line x1={cx(hover)} y1={mt} x2={cx(hover)} y2={mt + ph}
            stroke={ink.axis} strokeWidth={0.75} strokeDasharray="3 3" opacity={0.5} />
        )}

        {points.map((p, i) => (
          <rect key={`b${i}`} x={barX(i, false)} y={yBar(p.bar)} width={barW}
            height={Math.max(0, mt + ph - yBar(p.bar))} rx={6}
            fill={partial(i) ? 'none' : ink.barSoft}
            stroke={partial(i) ? AMBER : 'none'} strokeWidth={partial(i) ? 1.25 : 0}
            strokeDasharray={partial(i) ? '3 3' : undefined}
            style={{
              transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1), transform 160ms cubic-bezier(0.4, 0, 0.2, 1), filter 160ms cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: hover == null || hover === i ? 1 : 0.72,
              transform: hover === i ? 'translateY(-4px)' : 'translateY(0px)',
              filter: hover === i ? 'brightness(1.08)' : 'none',
            }} />
        ))}
        {/* [SECOND BAR 2026-08-17] Beside the first, on the SAME scale, so the two are directly
            comparable. Outlined rather than filled on the running month, matching the first. */}
        {hasBar2 && points.map((p, i) => (
          <rect key={`b2${i}`} x={barX(i, true)} y={yBar(p.bar2 ?? 0)} width={barW}
            height={Math.max(0, mt + ph - yBar(p.bar2 ?? 0))} rx={6}
            fill={partial(i) ? 'none' : (bar2Color ?? TEAL)}
            opacity={partial(i) ? 1 : 0.55}
            stroke={partial(i) ? AMBER : 'none'} strokeWidth={partial(i) ? 1.25 : 0}
            strokeDasharray={partial(i) ? '3 3' : undefined}
            style={{
              transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1), transform 160ms cubic-bezier(0.4, 0, 0.2, 1), filter 160ms cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: (partial(i) ? 1 : 0.55) * (hover == null || hover === i ? 1 : 0.72),
              transform: hover === i ? 'translateY(-4px)' : 'translateY(0px)',
              filter: hover === i ? 'brightness(1.08)' : 'none',
            }} />
        ))}
        {points.map((p, i) => (
          /* With two bars the label sits over the FIRST one rather than the band centre, where
             it would float between them and belong to neither. The tooltip carries both. */
          <text key={`bl${i}`} x={hasBar2 ? barX(i, false) + barW / 2 : cx(i)} y={yBar(p.bar) - 6} fontSize="9"
            fill={partial(i) ? AMBER : ink.label}
            textAnchor="middle" fontWeight={650}
            style={{ transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hover == null || hover === i ? 1 : 0.45 }}>
            {formatBar(p.bar)}
          </text>
        ))}

        {/* Curve, not a polyline — see smoothPath. The dots below still sit on the true values,
            so the curve is only the connection between them, never the reading. */}
        {/* [MOTION-C 2026-08-20] Draws itself in when the chart appears or its months change.
            Safe here because this line is SOLID: DrawnPath borrows strokeDasharray for the
            draw and clears it afterwards, so it must never be given a line whose dashes carry
            meaning (the partial-month dashes on the bars/dots above are left untouched). */}
        <DrawnPath d={line} fill="none" stroke={lineColor} strokeWidth={2}
          strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={`d${i}`} cx={cx(i)} cy={yLine(p.line)} r={hover === i ? 4.5 : 2.8}
            fill={partial(i) ? AMBER : lineColor} style={{ transition: 'r 120ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
        ))}

        {points.map((p, i) => (
          <text key={`x${i}`} x={cx(i)} y={H - 11} fontSize="10"
            fill={partial(i) ? AMBER : ink.strong} textAnchor="middle"
            fontWeight={hover === i || partial(i) ? 750 : 500}>
            {fmtMonth(p.month)}{partial(i) ? ' ·' : ''}
          </text>
        ))}

        <g transform={`translate(${tipX}, ${tipY})`} pointerEvents="none"
          style={{ transition: 'transform 120ms cubic-bezier(0.4, 0, 0.2, 1), opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hp ? 1 : 0 }}>
          <TipGlass w={tipW} h={tipH} dark={ink.dark} />
          {hp && (
            <>
              <text x={10} y={16} fontSize="9.5" fontWeight={750} fill={ink.strong}>{fmtMonthYear(hp.month)}</text>
              {tipRows.map((r, k) => (
                <text key={r.label} x={10} y={31 + k * 14} fontSize="9" fill={ink.tipLabel}>
                  {r.label}: <tspan fill={r.color} fontWeight={r.weight}>{r.value}</tspan>
                </text>
              ))}
              {progress && hp.month === progress.month && (
                <text x={10} y={33 + tipRows.length * 14} fontSize="8.5" fontWeight={700} fill={AMBER}>
                  Month to date — day {progress.day} of {progress.daysInMonth}
                </text>
              )}
            </>
          )}
        </g>

        {points.map((p, i) => (
          <rect key={`h${i}`} x={ml + band * i} y={mt} width={band} height={ph}
            fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
    </ChartScroll>
  );
};

const Legend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    gap: 6px 14px;
  }
`;

const LegendItem = styled.span<{ $color: string; $soft?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${inkText};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 12px;
  }

  &::before {
    content: '';
    width: 10px;
    height: ${(p) => (p.$soft ? '10px' : '3px')};
    border-radius: ${(p) => (p.$soft ? '3px' : '2px')};
    background: ${(p) => p.$color};
  }
`;

/* ── stacked revenue-mix chart ────────────────────────────────────────────────── */

/**
 * A rect with only its TOP two corners rounded — the cap of a stacked column.
 *
 * Rounding every segment would put a bite out of the column at each boundary and make the stack
 * read as three floating pills; rounding nothing leaves a hard rectangle that does not match the
 * rest of the page. Only the segment that actually ends the column is capped.
 */
const topRoundedPath = (x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.max(0, Math.min(r, h, w / 2));
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} `
    + `L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
};


/* ── money flow: gross to net, the EOM row as a chart ─────────────────────────── */

/**
 * [MONEY FLOW 2026-08-27] The two Revenue engine cards, clubbed. Above the axis the three
 * engines stacked to gross (same fills as the mix chart they replace, so nothing has to be
 * relearned); below it refunds and lost disputes; beside the stack a thin green bar for money won
 * back; and through it all the net — gross, less what came back out, plus what we kept back —
 * which is the EOM tab's Net, month by month, without a second tab.
 *
 * FULL WIDTH, so the viewBox is 1520 wide rather than the half-width cards' 760: at ~1.2x the
 * text sizes land where the neighbouring charts' do. ⚠ Keep this W/H, the skeleton `ratio` and the
 * tooltip sizes in step — viewBox units only mean anything against the rendered width.
 *
 * ONE SCALE for both halves. The outflow is drawn to the same dollars-per-unit as the inflow, so
 * a $24k bar under a $282k stack reads as the 8% it is. Making the lower half its own scale would
 * be the easy way to make it "visible" and the wrong way to make it honest.
 *
 * Labels, one per figure, each in its own position and colour so none is mistaken for another:
 * gross above the stack (muted, as the old chart printed it), net beside its dot (strong — the
 * headline of this card), outflow beneath its bar (red). The breakdown is a hover away.
 */
const FlowChart: React.FC<{
  points: PaMoneyFlowPoint[];
  /** The month still running — outlined, dot hollow, figures labelled "so far". */
  progress?: PaMonthProgress | null;
  /** [MONEY COMPARE 2026-08-31] The comparison cards feed this chart YEARS and QUARTERS, whose
   *  `month` keys are labels like "2024" or "Q2 2025" — these three let a caller re-word the
   *  axis, the tooltip title and the partial-period line without a second chart component. */
  labelOf?: (m: string) => string;
  titleOf?: (m: string) => string;
  partialLabel?: string;
  /** Half-width variant: 760-unit viewBox so text renders at card scale, like the other charts. */
  compact?: boolean;
}> = ({ points, progress, labelOf, titleOf, partialLabel, compact }) => {
  const ink = useInk();
  const theme = useTheme() as any;
  const [hover, setHover] = useState<number | null>(null);
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22]
  if (!points.length) return <Empty>No data for this period.</Empty>;
  const partial = (i: number) => !!progress && points[i].month === progress.month;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the compact height and 46 units per month (every label is centred on its month
     and needs ~30), so the chart drawn at phone size (x 1.2) reads at 10.8 px and shows about six months at a time;
     the full-width 1520 geometry would need 1824 px. */
  const W = isMobile ? Math.max(300, 28 + points.length * 46) : compact ? 760 : 1520;
  const H = isMobile || compact ? 320 : 360, ml = 14, mr = 14, mt = 30, mb = 34;
  const xLabel = labelOf ?? fmtMonth;
  const tipTitle = titleOf ?? fmtMonthYear;
  const pw = W - ml - mr, ph = H - mt - mb;
  const n = Math.max(points.length, 1);
  const band = pw / n;
  const barW = Math.min(band * 0.42, 60);
  const wonW = Math.max(8, Math.round(barW * 0.3));
  const cx = (i: number) => ml + band * i + band / 2;

  // Room below the axis: the tallest outflow bar plus its label. Shared scale — see the note.
  const maxUp = Math.max(...points.map((p) => p.gross), 1);
  const maxDown = Math.max(...points.map((p) => p.refundsDisputes), 0);
  const downLabel = 16;
  const scale = (ph - downLabel) / (maxUp + maxDown);
  const h = (v: number) => v * scale;
  const zeroY = mt + h(maxUp);

  const seriesOf = (p: PaMoneyFlowPoint) => [
    { key: 'Retention', value: p.retention, color: ink.mix.retention },
    { key: 'Upsell / SO', value: p.upsell, color: ink.mix.upsell },
    { key: 'New sales', value: p.newSales, color: ink.mix.newSales },
    // [PIA IN GROSS 2026-09-01, user] The fourth bucket EOM carries inside Gross.
    { key: 'PIA', value: p.pia ?? 0, color: ink.mix.pia },
    // [SHOPIFY 2026-10-19, user] The EOM tab's Shopify line, inside its Gross — so the stack reaches Gross again.
    { key: 'Shopify', value: p.shopify ?? 0, color: ink.mix.shopify },
  ];

  const netPts = points.map((p, i) => ({ x: cx(i), y: zeroY - h(p.net) }));
  const netPath = netPts.length > 1 ? smoothPath(netPts) : '';

  const hp = hover != null ? points[hover] : null;
  // The tooltip reads the way the EOM row is built: the three engines, their gross, what came
  // out, what came back, and the net they leave.
  const tipRows = hp
    ? [
      ...seriesOf(hp).reverse().map((s) => ({ label: s.key, value: fmtMoney(s.value), color: s.color })),
      { label: 'Gross', value: fmtMoney(hp.gross), color: ink.strong },
      { label: 'Refunds + lost disputes', value: `−${fmtMoney(hp.refundsDisputes)}`, color: RED },
      { label: 'Won / recovery', value: `+${fmtMoney(hp.disputesWonRecovery)}`, color: ink.green },
      { label: 'Net kept', value: fmtMoney(hp.net), color: ink.strong },
    ]
    : [];
  const tipW = 200;
  const tipRowH = 14;
  const tipH = 26 + tipRows.length * tipRowH + (hover != null && partial(hover) ? 16 : 0);
  const tipX = hover != null ? Math.max(4, Math.min(W - tipW - 4, cx(hover) - tipW / 2)) : 0;
  const topY = hp ? zeroY - h(hp.gross) : 0;
  const tipY = topY - tipH - 14 < 4 ? topY + 18 : topY - tipH - 14;

  return (
    <ChartScroll>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? phoneChartWidth(W) : (compact ? 560 : 900) /* [PHONE-LAYOUT 2026-10-22] */ }} onMouseLeave={() => setHover(null)}>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={ml} y1={zeroY - h(maxUp) * f} x2={W - mr} y2={zeroY - h(maxUp) * f}
            stroke={ink.grid} strokeWidth={1} />
        ))}
        {/* The zero line carries the meaning here, so it is the one axis drawn at full weight. */}
        <line x1={ml} y1={zeroY} x2={W - mr} y2={zeroY} stroke={ink.axis} strokeWidth={1} />

        {points.map((p, i) => {
          const dim = hover != null && hover !== i;
          const x = cx(i) - barW / 2;
          let acc = 0;
          const segs = seriesOf(p);
          /* [PIA IN GROSS 2026-09-01] The rounded cap belongs to the top DRAWN segment, not to
             the last one in the array. PIA is legitimately $0 in some months (Jul 2026), and any
             bucket can be, so keying the cap on the array index left those columns square-topped
             while their neighbours were rounded. Same reason the inter-segment gap is measured
             against this index rather than `segs.length - 1`. */
          const segH = segs.map((s) => h(s.value));
          const topIdx = segH.reduce((acc2, raw, k) => (raw > 0.4 ? k : acc2), -1);
          const down = h(p.refundsDisputes);
          const won = h(p.disputesWonRecovery);
          return (
            // [PREMIUM HOVER 2026-08-31, user] Subtle dim + brighten; no lift here — the net
            // line pins every column's dot, and a lifted bar would float away from it.
            <g key={`s${i}`} style={{
              transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1), filter 160ms cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: dim ? 0.72 : 1,
              filter: hover === i ? 'brightness(1.08)' : 'none',
            }}>
              {segs.map((s, k) => {
                const yTop = zeroY - h(acc + s.value);
                const raw = h(s.value);
                acc += s.value;
                const gap = k < topIdx && raw > 4 ? 2 : 0;
                const height = raw - gap;
                if (height <= 0.4) return null;
                const isCap = k === topIdx;
                return isCap
                  ? <path key={s.key} d={topRoundedPath(x, yTop, barW, height, 10)} fill={s.color}
                      opacity={partial(i) ? 0.45 : 1} />
                  : <rect key={s.key} x={x} y={yTop + gap} width={barW} height={height} fill={s.color}
                      opacity={partial(i) ? 0.45 : 1} />;
              })}
              {/* Outflow, hung from the axis. Drawn even when tiny (min 1.5 units) so a month with
                  almost nothing coming back out still shows AS a month with almost nothing, not
                  as a gap in the series. */}
              {p.refundsDisputes > 0 && (
                <rect x={x} y={zeroY + 1} width={barW} height={Math.max(1.5, down)} rx={5}
                  fill={ink.outflow} opacity={partial(i) ? 0.55 : 1} />
              )}
              {/* Money won back: positive, so it stands ABOVE the axis, beside the stack rather
                  than on top of it — stacking it would make the bar's top stop meaning gross. */}
              {p.disputesWonRecovery > 0 && (
                <rect x={cx(i) + barW / 2 + 4} y={zeroY - Math.max(1.5, won)} width={wonW}
                  height={Math.max(1.5, won)} rx={2} fill={ink.greenFill} opacity={partial(i) ? 0.55 : 0.9} />
              )}
              {partial(i) && (
                <rect x={x - 1.5} y={zeroY - h(p.gross) - 1.5} width={barW + 3} height={h(p.gross) + 3}
                  rx={5} fill="none" stroke={AMBER} strokeWidth={1.25} strokeDasharray="3 3" />
              )}
              {/* [GROSS ON TOP 2026-08-31, user — supersedes KEPT ON TOP earlier today] "bar
                  will show gross values and the dot will show the net value": gross above the
                  stack (muted), net beside its dot (strong) — the arrangement the card's doc
                  comment describes. */}
              {/* [FULL-INK 2026-09-01, user] Gross was axis-muted to let net lead; the user
                  chose full ink here too — position and the net label's halo now carry the
                  distinction. */}
              <text x={cx(i)} y={zeroY - h(p.gross) - 8} fontSize="9" textAnchor="middle"
                fontWeight={650} fill={partial(i) ? AMBER : ink.strong}>
                {fmtMoneyShort(p.gross)}
              </text>
              {/* Outflow beneath its bar, in the hue of the bar it belongs to. */}
              {p.refundsDisputes > 0 && (
                <text x={cx(i)} y={zeroY + Math.max(1.5, down) + 11} fontSize="9" textAnchor="middle"
                  fontWeight={650} fill={partial(i) ? AMBER : RED}>
                  −{fmtMoneyShort(p.refundsDisputes)}
                </text>
              )}
            </g>
          );
        })}

        {/* The net, drawn last so it sits over the stacks: a line for the trend, a dot per month,
            and the figure beside each dot — to the LEFT, because the won/recovery bar owns the
            right. Strong ink, not a series colour: it is the sum of the others, not one of them. */}
        {netPath && (
          <path d={netPath} fill="none" stroke={ink.strong} strokeWidth={1.6} strokeLinejoin="round"
            strokeLinecap="round" opacity={0.85} />
        )}
        {points.map((p, i) => (
          <g key={`n${i}`}>
            <circle cx={cx(i)} cy={zeroY - h(p.net)} r={hover === i ? 5 : 3.6}
              fill={partial(i) ? AMBER : ink.strong}
              stroke={ink.tipBg} strokeWidth={1.5}
              style={{ transition: 'r 120ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
            {/* [NET BELOW DOT 2026-09-01, user] The kept figure hangs centred UNDER its dot —
                "gross above the bar, kept below the dot". Flips above the dot when the dot
                rides close to the axis (a part month's tiny net) so the label never falls
                into the outflow zone. */}
            {/* [KEPT HALO 2026-09-01, user] The label usually lands ON the bar fill, where
                strong ink washes out. A stroke in the tooltip-surface tone rendered BEHIND the
                fill (paint-order) backs it against any ground — bar, background or line — in
                both themes; it is the same colour pairing the dot's own ring uses. */}
            <text x={cx(i)} y={h(p.net) < 26 ? zeroY - h(p.net) - 9 : zeroY - h(p.net) + 17}
              fontSize="9" textAnchor="middle"
              fontWeight={750} fill={partial(i) ? AMBER : ink.strong}
              stroke={ink.tipBg} strokeWidth={3} strokeLinejoin="round"
              style={{ paintOrder: 'stroke' }}>
              {fmtMoneyShort(p.net)}
            </text>
          </g>
        ))}

        {points.map((p, i) => (
          <text key={`x${i}`} x={cx(i)} y={H - 11} fontSize="10"
            fill={partial(i) ? AMBER : ink.strong} textAnchor="middle"
            fontWeight={hover === i || partial(i) ? 750 : 500}>
            {xLabel(p.month)}{partial(i) ? ' ·' : ''}
          </text>
        ))}

        <g transform={`translate(${tipX}, ${tipY})`} pointerEvents="none"
          style={{ transition: 'transform 120ms cubic-bezier(0.4, 0, 0.2, 1), opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hp ? 1 : 0 }}>
          <TipGlass w={tipW} h={tipH} dark={ink.dark} />
          {hp && (
            <>
              <text x={10} y={16} fontSize="9.5" fontWeight={750} fill={ink.strong}>{tipTitle(hp.month)}</text>
              {tipRows.map((r, k) => (
                <text key={r.label} x={10} y={31 + k * tipRowH} fontSize="9" fill={ink.tipLabel}>
                  {r.label}: <tspan fill={r.color} fontWeight={750}>{r.value}</tspan>
                </text>
              ))}
              {progress && hp.month === progress.month && (
                <text x={10} y={33 + tipRows.length * tipRowH} fontSize="8.5" fontWeight={700} fill={AMBER}>
                  {partialLabel ?? `Day ${progress.day} of ${progress.daysInMonth}`}
                </text>
              )}
            </>
          )}
        </g>

        {points.map((p, i) => (
          <rect key={`h${i}`} x={ml + band * i} y={0} width={band} height={H}
            fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
    </ChartScroll>
  );
};

/* ── money compare: year / quarter / month rollups of the compare series ──────── */

/**
 * [MONEY COMPARE 2026-08-31] Sum a slice of the monthly compare series into one synthetic
 * "month" whose key is the row's label ("2024", "Q2 2025", "Jun 2026"). Every field is a plain
 * sum and Net is linear, so the sum of nets IS the net of sums — the server's per-month formula
 * survives aggregation untouched, and no second arithmetic exists on the client.
 */
const sumComparePoints = (pts: PaMoneyComparePoint[], label: string): PaMoneyFlowPoint | null => {
  if (!pts.length) return null;
  const s: PaMoneyFlowPoint = {
    month: label, retention: 0, upsell: 0, newSales: 0, pia: 0, shopify: 0, gross: 0,
    refundsDisputes: 0, disputesWonRecovery: 0, net: 0,
  };
  for (const p of pts) {
    s.retention += p.retention; s.upsell += p.upsell; s.newSales += p.newSales;
    s.pia += p.pia ?? 0; s.gross += p.gross;
    s.shopify = (s.shopify ?? 0) + (p.shopify ?? 0); // [SHOPIFY 2026-10-19]
    s.refundsDisputes += p.refundsDisputes; s.disputesWonRecovery += p.disputesWonRecovery; s.net += p.net;
  }
  return s;
};

/**
 * [MONEY COMPARE V2 2026-08-31, user] "make these graphs vertical bar only ... the quarter graph
 * will have all the quarters ... and for the month we will have the complete row covered with
 * all the months" — so the pickers are GONE and everything is on screen at once: grouped
 * vertical bars, one group per period, one bar per year inside it. Each bar keeps the money-flow
 * idiom — engines stacked above the axis, refunds hanging below, the net dot — and the full
 * breakdown lives in the hover tooltip.
 *
 * [GROSS ON TOP 2026-08-31, user] Every bar prints its GROSS above it and its NET beside the dot,
 * the same two-figure arrangement as the monthly FlowChart. `denseLabels` (>12 bars) drops both
 * to 8px and hides the outflow figure rather than dropping a whole reading.
 */
const FlowGroups: React.FC<{
  groups: { label: string; points: PaMoneyFlowPoint[] }[];
  /** viewBox width — 460 for the 30% card, 1060 for the 70% card, 1520 full row. */
  viewW: number;
  /** Bar keys ("Q3 2026", "Aug 2026", "2026") still filling in — dashed amber. */
  partialKey?: string | null;
  partialLabel?: string;
  /** Bar x-labels; defaults to the trailing year, shortened ('24 '25 '26). */
  barLabelOf?: (m: string) => string;
  titleOf?: (m: string) => string;
  /** [2026-08-31, user] Year card only: distribute the bars across the full width — three
   *  clustered bars in a lone group left "so much waste space" either side and collided the
   *  span labels beneath. Dense multi-group charts keep the tight-cluster geometry. */
  spread?: boolean;
  /**
   * [YEAR-ON-YEAR 2026-09-01, user] Where the ▲▼ change figure goes.
   *
   * 'group' (default) puts ONE figure on the cluster label — the change between the cluster's
   * last two complete years.
   *
   * 'bar' moves it onto each bar's own x-label instead, so every bar states its change against
   * the bar to its left: the same period a year earlier. The user's reason for the quarter card:
   * "the bar graph is already in quarter thing" — the cluster label was answering a question the
   * grouping had already answered, while the year-on-year step between bars went unlabelled.
   */
  deltaOn?: 'group' | 'bar';
}> = ({ groups, viewW, partialKey, partialLabel, barLabelOf, titleOf, spread, deltaOn = 'group' }) => {
  const ink = useInk();
  const theme = useTheme() as any;
  const [hover, setHover] = useState<string | null>(null);
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22] the chart's phone width
  // `prev` is the bar to the left WITHIN THE SAME GROUP — the same period a year earlier, which
  // is what deltaOn='bar' measures against. Null on a group's first bar, which has nothing to
  // compare with rather than a change of zero.
  const flat = groups.flatMap((g) => g.points.map((p, k) => ({
    g: g.label, p, prev: k > 0 ? g.points[k - 1] : null,
  })));
  if (!flat.length) return <Empty>No data for this period.</Empty>;

  // Hoisted above the geometry: the spacing below depends on it, because a dense chart handles a
  // long x-label by stacking rather than by spreading.
  const denseLabels = flat.length > 12;
  /* [YEAR-ON-YEAR 2026-09-01, user] Where a bar's change figure goes when deltaOn='bar':
     BESIDE the year on a roomy chart (the 10-bar quarter one), and on its OWN LINE beneath the
     year on a dense one (the 33-bar month chart). Inline there would need ~36 units against a
     ~44-unit pitch and the labels would touch, and the horizontal fix the quarter chart uses —
     spreading the bars — cannot work here: its cluster gap is already down to 18, so widening
     the intra-bar gap to match would collapse twelve clusters into one row. */
  const stackDelta = deltaOn === 'bar' && denseLabels;
  const wideLabels = deltaOn === 'bar' && !denseLabels;

  const W = viewW, H = 300, ml = 14, mr = 14, mt = 26, mb = groups.some((g) => g.label) ? 44 : 30;
  const ph = H - mt - mb;
  const maxUp = Math.max(...flat.map(({ p }) => p.gross), 1);
  const maxDown = Math.max(...flat.map(({ p }) => p.refundsDisputes), 0);
  const downLabel = 14;
  const s = (ph - downLabel) / (maxUp + maxDown);
  const h = (v: number) => v * s;
  const zeroY = mt + h(maxUp);

  /* [CLUSTER READABILITY 2026-08-31, user] "the quarter part are not clear" — the old spacing
     gave a cluster barely more air than its own bars, so ten bars read as one row. Bars inside
     a cluster now nearly touch and clusters stand well apart, which turns the within-cluster
     year comparison into an adjacent-bar read instead of a label hunt. */
  /* [YEAR-ON-YEAR SPACING 2026-09-01, user] "make the size a little horizontally so that the x
     axis text should come in the middle side of the bars, otherwise it will merge."

     Under deltaOn='bar' each x-label grew from a bare year (~21 units) to a year plus its change
     (~42, and ~47 at two digits) while the bar pitch stayed barW + 5 = 51 — so neighbouring
     labels were within a few units of touching. Widening the intra-group gap moves the pitch to
     ~58 and gives every label room to sit centred under its own bar.

     The cluster gap widens WITH it, deliberately. The 2026-08-31 rule is a RATIO, not a number:
     bars inside a cluster sit close and clusters stand well apart, so raising intra alone would
     have flattened ten bars back into one row — the exact "the quarter part are not clear"
     complaint that spacing was built to fix. 12 against 52 keeps the same reading.

     Untouched for the group-label charts, whose labels are still a bare year, and for the dense
     one, which stacks its figure instead and so needs no extra width. */
  const intra = wideLabels ? 12 : 5;
  const groupGap = flat.length > 20 ? 18 : (wideLabels ? 52 : 40);
  const plotW = W - ml - mr;
  const barW = spread
    ? Math.min(plotW / flat.length * 0.42, 64)
    : Math.min(
      (plotW - groupGap * (groups.length - 1) - intra * (flat.length - groups.length)) / flat.length, 46);
  const xs: number[] = [];
  const spans: { from: number; to: number }[] = [];
  if (spread) {
    const slot = plotW / flat.length;
    for (let i = 0; i < flat.length; i++) xs.push(ml + slot * i + slot / 2);
    spans.push({ from: ml, to: W - mr });
  } else {
    let x = ml;
    for (const g of groups) {
      const from = x;
      for (let k = 0; k < g.points.length; k++) {
        xs.push(x + barW / 2);
        x += barW + (k < g.points.length - 1 ? intra : 0);
      }
      spans.push({ from, to: x });
      x += groupGap;
    }
    const used = x - groupGap;
    const shift = (plotW - (used - ml)) / 2;
    if (shift > 0) { for (let i = 0; i < xs.length; i++) xs[i] += shift; for (const sp of spans) { sp.from += shift; sp.to += shift; } }
  }
  /* [YEAR RAMP 2026-08-31, user's pick] One green per year, palest to fullest — the comparison
     dimension is the YEAR, so the year is what gets painted. The engine mix lives in the
     tooltip (and on the monthly chart, its real home); pre-2026 mix is partly deal-sourced, so
     a single tone also stops the chart overselling how precisely those years are known. */
  const yearFill = (m: string) => {
    const y = m.slice(-4);
    return y === '2026' ? ink.greenFill
      : y === '2025' ? (ink.dark ? 'rgba(14,126,78,0.60)' : 'rgba(26,127,100,0.60)')
      : (ink.dark ? 'rgba(14,126,78,0.34)' : 'rgba(26,127,100,0.34)');
  };
  // [2026-08-31, user] Full years on the axis — "replace 25 to '2025'". The dense month
  // chart's slots are wide enough for four digits, so one rule serves every chart.
  const yearShort = (m: string) => m.slice(-4);
  const xLabel = barLabelOf ?? yearShort;
  const tipTitle = titleOf ?? ((m: string) => m);
  const partial = (p: PaMoneyFlowPoint) => !!partialKey && p.month === partialKey;

  /**
   * [YEAR-ON-YEAR 2026-09-01, user] One bar's change in NET — the money kept, the dot — against
   * the bar to its left in the same group.
   *
   * Returns null rather than a number in three cases, each of which would otherwise print a
   * figure that is not a like-for-like comparison:
   *   • no bar to the left (a group's first year has nothing to compare with),
   *   • either side is a part-finished period — a few months measured against twelve is the
   *     false collapse the cluster verdict has always excluded, so bars exclude it too,
   *   • the earlier bar kept nothing, where a percentage change is undefined, not infinite.
   */
  const barDelta = (p: PaMoneyFlowPoint, prev: PaMoneyFlowPoint | null): number | null => {
    if (deltaOn !== 'bar' || !prev) return null;
    if (partial(p) || partial(prev)) return null;
    if (!(prev.net > 0)) return null;
    return ((p.net - prev.net) / prev.net) * 100;
  };

  const hi = hover != null ? flat.findIndex(({ p }) => p.month === hover) : -1;
  const hp = hi >= 0 ? flat[hi].p : null;
  const tipRows = hp
    ? [
      { label: 'Retention', value: fmtMoney(hp.retention), color: ink.mix.retention },
      { label: 'Upsell / SO', value: fmtMoney(hp.upsell), color: ink.mix.upsell },
      { label: 'New sales', value: fmtMoney(hp.newSales), color: ink.mix.newSales },
      // [PIA IN GROSS 2026-09-01, user] Listed unconditionally: a month with no PIA reads $0.00,
      // which is a fact about that month, where a missing row reads as "not measured".
      { label: 'PIA', value: fmtMoney(hp.pia ?? 0), color: ink.mix.pia },
      { label: 'Shopify', value: fmtMoney(hp.shopify ?? 0), color: ink.mix.shopify }, // [SHOPIFY 2026-10-19] inside Gross
      { label: 'Gross', value: fmtMoney(hp.gross), color: ink.strong },
      { label: 'Refunds + lost disputes', value: `−${fmtMoney(hp.refundsDisputes)}`, color: RED },
      { label: 'Won / recovery', value: `+${fmtMoney(hp.disputesWonRecovery)}`, color: ink.greenFill },
      { label: 'Net kept', value: fmtMoney(hp.net), color: ink.strong },
    ]
    : [];
  const tipW = 190, tipRowH = 13;
  const tipH = 24 + tipRows.length * tipRowH + (hp && partial(hp) && partialLabel ? 15 : 0);
  const tipX = hi >= 0 ? Math.max(4, Math.min(W - tipW - 4, xs[hi] - tipW / 2)) : 0;
  const tipY = hp ? Math.max(4, zeroY - h(hp.gross) - tipH - 12) : 0;

  return (
    <ChartScroll>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? phoneChartWidth(W, denseLabels ? 1.34 : PHONE_CHART_SCALE) : Math.min(560, W) /* [PHONE-LAYOUT 2026-10-22] */ }} onMouseLeave={() => setHover(null)}>
        {/* [2026-08-31, user] The alternating band behind every other cluster is GONE ("remove
            the grey bg") — the tight-cluster spacing and the captions carry the grouping now. */}
        <line x1={ml} y1={zeroY} x2={W - mr} y2={zeroY} stroke={ink.axis} strokeWidth={1} />
        {groups.some((g) => g.label) && (() => {
          const ys = [...new Set(flat.map(({ p }) => p.month.slice(-4)))].sort();
          return ys.map((y, i) => (
            <g key={y} transform={`translate(${W - mr - (ys.length - i) * 56}, 4)`}>
              <rect width={10} height={10} rx={2} fill={yearFill(y)} />
              <text x={14} y={9} fontSize="9" fill={ink.strong} fontWeight={650}>{y}</text>
            </g>
          ));
        })()}

        {flat.map(({ p, prev }, i) => {
          const x = xs[i] - barW / 2;
          const down = h(p.refundsDisputes);
          const gh = h(p.gross);
          const yoy = barDelta(p, prev);
          return (
            <g key={p.month} style={{
              transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: hover != null && hover !== p.month ? 0.72 : 1,
            }}>
              {/* [PREMIUM HOVER 2026-08-31, user, twice] The hovered column LIFTS ~4px with a
                  gentle brighten, tooltip gliding in above — and the rest take a SUBTLE dim
                  (0.72, against the old heavy 0.45 that read as blur) so the selection is
                  unmistakable. Standard app easing, no overshoot; the x-label stays anchored
                  so the axis row never jiggles. */}
              <g style={{
                transition: 'transform 160ms cubic-bezier(0.4, 0, 0.2, 1)',
                transform: hover === p.month ? 'translateY(-4px)' : 'translateY(0px)',
              }}>
              {gh > 0.4 && (
                <path d={topRoundedPath(x, zeroY - gh, barW, gh, 8)} fill={yearFill(p.month)}
                  opacity={partial(p) ? 0.55 : 1}
                  style={{ transition: 'filter 160ms cubic-bezier(0.4, 0, 0.2, 1)', filter: hover === p.month ? 'brightness(1.1)' : 'none' }} />
              )}
              {p.refundsDisputes > 0 && (
                <rect x={x} y={zeroY + 1} width={barW} height={Math.max(1.5, down)} rx={4}
                  fill={ink.outflow} opacity={partial(p) ? 0.55 : 1} />
              )}
              {partial(p) && (
                <rect x={x - 1.5} y={zeroY - h(p.gross) - 1.5} width={barW + 3} height={h(p.gross) + 3}
                  rx={4} fill="none" stroke={AMBER} strokeWidth={1.25} strokeDasharray="3 3" />
              )}
              {/* [GROSS ON TOP 2026-08-31, user — supersedes KEPT ON TOP] "below charts also
                  show gross on the bars and dot will show the net values" — same arrangement
                  as the monthly chart. */}
              <text x={xs[i]} y={zeroY - h(p.gross) - 6} fontSize={denseLabels ? 8 : 9} textAnchor="middle"
                fontWeight={650} fill={partial(p) ? AMBER : ink.strong}>
                {fmtMoneyShort(p.gross)}
              </text>
              {p.refundsDisputes > 0 && !denseLabels && (
                <text x={xs[i]} y={zeroY + Math.max(1.5, down) + 10} fontSize="8.5" textAnchor="middle"
                  fontWeight={650} fill={partial(p) ? AMBER : RED}>
                  −{fmtMoneyShort(p.refundsDisputes)}
                </text>
              )}
              <circle cx={xs[i]} cy={zeroY - h(p.net)} r={hover === p.month ? 4.5 : 3.2}
                fill={partial(p) ? AMBER : ink.strong} stroke={theme.surface} strokeWidth={1.5}
                style={{ transition: 'r 120ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
              {/* [NET BELOW DOT 2026-09-01, user] Centred under the dot, same rule as the
                  monthly chart (and kinder to the 33-bar month chart than the old left-of-dot
                  placement); dense charts drop to the same 8px their top labels use. */}
              {/* [KEPT HALO 2026-09-01] Same backing stroke as the monthly chart — the label
                  sits on the bar fill and needs it in both themes. */}
              <text x={xs[i]} y={h(p.net) < 26 ? zeroY - h(p.net) - 8 : zeroY - h(p.net) + 15}
                fontSize={denseLabels ? 8 : 9}
                textAnchor="middle" fontWeight={750} fill={partial(p) ? AMBER : ink.strong}
                stroke={ink.tipBg} strokeWidth={3} strokeLinejoin="round"
                style={{ paintOrder: 'stroke' }}>
                {fmtMoneyShort(p.net)}
              </text>
              </g>
              {/* [YEAR-ON-YEAR 2026-09-01, user] The change rides on the YEAR, a size down so the
                  year stays the label and the figure reads as its qualifier. Beside it where
                  there is room (quarter chart: ~46-unit bars on ~58-unit centres against a
                  ~42-unit label), on its own line beneath where there is not (month chart). */}
              <text x={xs[i]} y={H - (groups.some((g) => g.label) ? 26 : 12)} fontSize={denseLabels ? 8.5 : 9.5}
                fill={partial(p) ? AMBER : ink.strong} textAnchor="middle"
                fontWeight={hover === p.month || partial(p) ? 750 : 500}>
                {xLabel(p.month)}{partial(p) ? ' ·' : ''}
                {yoy != null && !stackDelta && (
                  <tspan fontSize={8} fontWeight={750}
                    fill={yoy >= 0 ? ink.green : (ink.dark ? '#e0705f' : RED)}>
                    {' '}{yoy >= 0 ? '▲' : '▼'}{Math.abs(yoy).toFixed(0)}%
                  </tspan>
                )}
              </text>
              {/* [ABOVE THE YEAR 2026-09-01, user] The stacked variant sits ABOVE the year, not
                  below it: below would wedge it between the year and the month name, where three
                  stacked lines read as one block. Above, it has clear air — a dense chart prints
                  no outflow figures, so nothing occupies that band.

                  The offset is 15 units, not the 8 it started at. At 8 the two baselines put the
                  figure's descenders on the year's cap height and the user read them as touching
                  ("right now it is so attached"); 15 leaves a clear band between them and still
                  ends ~17 units clear of the tallest outflow bar. */}
              {yoy != null && stackDelta && (
                <text x={xs[i]} y={H - (groups.some((g) => g.label) ? 41 : 27)} fontSize={7.5}
                  textAnchor="middle" fontWeight={750}
                  fill={yoy >= 0 ? ink.green : (ink.dark ? '#e0705f' : RED)}>
                  {yoy >= 0 ? '▲' : '▼'}{Math.abs(yoy).toFixed(0)}%
                </text>
              )}
            </g>
          );
        })}

        {/* [CLUSTER READABILITY 2026-08-31] The caption carries the VERDICT: net growth between
            the cluster's latest two COMPLETE years, so a half-filled 2026 can never fake a
            fall. The reader came to ask "is this quarter growing?" — so the answer is printed. */}
        {(() => {
          let idx = 0;
          return groups.map((g) => {
            const from = xs[idx]; const to = xs[idx + g.points.length - 1];
            const complete = g.points.filter((p) => !partial(p));
            idx += g.points.length;
            if (!g.label) return null;
            // [YEAR-ON-YEAR 2026-09-01, user] Under deltaOn='bar' the cluster label carries NO
            // figure — every bar states its own, and printing both would put two different
            // percentages on one cluster for the reader to reconcile.
            let delta: number | null = null;
            if (deltaOn === 'group' && complete.length >= 2) {
              const p0 = complete[complete.length - 2].net, p1 = complete[complete.length - 1].net;
              if (p0 > 0) delta = ((p1 - p0) / p0) * 100;
            }
            return (
              <text key={g.label} x={(from + to) / 2} y={H - 9} fontSize="10.5" textAnchor="middle"
                fontWeight={750} fill={ink.strong}>
                {g.label}
                {delta != null && (
                  <tspan fontSize="9" fontWeight={750}
                    fill={delta >= 0 ? ink.green : (ink.dark ? '#e0705f' : RED)}>
                    {'  '}{delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}%
                  </tspan>
                )}
              </text>
            );
          });
        })()}

        <g transform={`translate(${tipX}, ${tipY})`} pointerEvents="none"
          style={{ transition: 'transform 120ms cubic-bezier(0.4, 0, 0.2, 1), opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hp ? 1 : 0 }}>
          <TipGlass w={tipW} h={tipH} dark={ink.dark} />
          {hp && (
            <>
              <text x={9} y={15} fontSize="9" fontWeight={750} fill={ink.strong}>{tipTitle(hp.month)}</text>
              {tipRows.map((r, k) => (
                <text key={r.label} x={9} y={28 + k * tipRowH} fontSize="8.5" fill={ink.tipLabel}>
                  {r.label}: <tspan fill={r.color} fontWeight={750}>{r.value}</tspan>
                </text>
              ))}
              {partial(hp) && partialLabel && (
                <text x={9} y={30 + tipRows.length * tipRowH} fontSize="8" fontWeight={700} fill={AMBER}>
                  {partialLabel}
                </text>
              )}
            </>
          )}
        </g>

        {flat.map(({ p }, i) => (
          <rect key={`h${p.month}`} x={xs[i] - barW / 2 - intra / 2} y={0} width={barW + intra} height={H}
            fill="transparent" onMouseEnter={() => setHover(p.month)} />
        ))}
      </svg>
    </ChartScroll>
  );
};

/** [MONEY COMPARE V2 2026-08-31] The 30 / 70 split row the user specified for year | quarters. */
const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 3fr 7fr;
  gap: 14px;
  align-items: stretch;
  ${layoutMax(900)} {
    grid-template-columns: minmax(0, 1fr);
  }
`;


/* ── gained / lost diverging chart ────────────────────────────────────────────── */

const ClientBaseChart: React.FC<{
  points: PaClientBasePoint[];
  /** The month still running — its gains and losses are only partly in, so it is marked. */
  progress?: PaMonthProgress | null;
  /** The month whose breakdown the panel beside the chart is showing — its column gets a band. */
  selected?: string | null;
  /** Click a column to load that month into the breakdown panel. */
  onPick?: (month: string) => void;
}> = ({ points, progress, selected, onPick }) => {
  const ink = useInk();
  const [hover, setHover] = useState<number | null>(null);
  // [FLUID-8 2026-10-12, user] A month turns bold on the mouse PRESS and the breakdown beside it follows as a transition —
  // it used to wait for the release AND a re-render of the whole Process Analysis page. Touch / keyboard: the click.
  const [picking, setPicking] = useState<string | null>(null);
  const [pickPending, startPick] = useTransition();
  useEffect(() => { if (!pickPending) setPicking(null); }, [pickPending, selected]);
  const monthPressedRef = useRef(false);
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22] the chart's phone width
  const shownSel = picking ?? selected;
  const pickMonth = (m: string) => {
    if (!onPick || m === shownSel || pickPending) return;
    setPicking(m);
    startPick(() => onPick(m));
  };
  if (!points.length) return <Empty>No data for this period.</Empty>;
  const partial = (i: number) => !!progress && points[i].month === progress.month;

  /* [MASTER-DETAIL 2026-08-27] This chart moved from full width into the left half of a 2-column
     Grid ("it takes a lot of space"), so the old full-width height juggling (H=200 desktop /
     270 mobile) is gone: rendered height is cardWidth × H/W, and at half width H=270 lands
     around 260px on a desktop — bars readable, card compact. Mobile renders the same viewBox
     over the ChartScroll minWidth and scrolls sideways, exactly as before. */
  /* [PHONE-LAYOUT 2026-10-22] Phones: 56 units per month instead of ~73 (the pair of bars and the side counts need
     about 46), so the chart drawn at phone size (x 1.2) shows four to five months at a time. */
  const W = isMobile ? Math.max(300, 28 + points.length * 56) : 760, H = 270, ml = 14, mr = 14, mt = 26, mb = 40;
  const pw = W - ml - mr, ph = H - mt - mb;
  const mid = mt + ph / 2;
  const half = ph / 2;
  const max = Math.max(...points.flatMap((p) => [p.gained, p.lost]), 1);
  const band = pw / points.length;
  const barW = Math.min(band * 0.34, 26);
  const cx = (i: number) => ml + band * i + band / 2;
  const h = (v: number) => (v / max) * (half - 12);

  return (
    <ChartScroll>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? phoneChartWidth(W) : 560 /* [PHONE-LAYOUT 2026-10-22] */ }} onMouseLeave={() => setHover(null)}>
        <line x1={ml} y1={mid} x2={W - mr} y2={mid} stroke={ink.axis} strokeWidth={0.75} />
        {points.map((p, i) => {
          const dim = hover != null && hover !== i;
          return (
            // [PREMIUM HOVER 2026-08-31, user] Same as the comparison charts: subtle dim, the
            // hovered column lifts and brightens; labels below sit in separate maps, so the
            // axis row stays still.
            <g key={i} style={{
              transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1), transform 160ms cubic-bezier(0.4, 0, 0.2, 1), filter 160ms cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: dim ? 0.72 : 1,
              transform: hover === i ? 'translateY(-4px)' : 'translateY(0px)',
              filter: hover === i ? 'brightness(1.08)' : 'none',
            }}>
              {/* [MASTER-DETAIL 2026-08-27, user] There was a full-height band behind the
                  selected column here; the user saw it and said "remove this no need of that".
                  Selection now shows only in the month label below (strong ink, bold) — and in
                  the panel heading itself, which names the month. */}
              {/* gained rises from the axis, lost falls from it — the shape reads before the numbers do */}
              {/* [SMOOTH 2026-08-31, user] rx up from 3 — the square shoulders read "boxy". */}
              <rect x={cx(i) - barW - 2} y={mid - h(p.gained)} width={barW} height={h(p.gained)}
                fill={ink.greenFill} rx={6} opacity={partial(i) ? 0.4 : 1} />
              <rect x={cx(i) + 2} y={mid} width={barW} height={h(p.lost)} fill={RED} rx={6}
                opacity={partial(i) ? 0.35 : 0.85} />
              {/* [SECOND-MONTH 2026-08-27, user] The red bar is DIVIDED: the paler slice against
                  the axis is campaigns that arrived AND were cancelled inside the month — a
                  "Second Month Cancellation" (their term: sold in M0, first inventory month M1,
                  gone in M1) — with its own count beside it; the solid red below is campaigns
                  that were already on the book. Drawn as an overlay on the full bar rather than
                  two stacked rects, so the tip's rounded corners stay as they were. The slice
                  sits at the AXIS on purpose: it pairs with the green bar it came out of.
                  [SECOND-MONTH 2026-08-31, user] The already-on-the-book count now prints too,
                  beside the SOLID segment in its own colour — the user pointed out that with only
                  the slice count and the −total, "for the rest if someone wants to see then he has
                  to make the calculations manually". On short bars the lower label is pushed down
                  so the two side counts never overprint each other. */}
              {(p.lostSameMonth ?? 0) > 0 && (
                <>
                  <rect x={cx(i) + 2} y={mid} width={barW} height={h(p.lostSameMonth!)} rx={3}
                    fill={ink.redSoft} opacity={partial(i) ? 0.5 : 1} />
                  <text x={cx(i) + barW + 6} y={mid + h(p.lostSameMonth!) / 2 + 3} fontSize="8.5"
                    fill={ink.redSoft} textAnchor="start" fontWeight={750}>{p.lostSameMonth}</text>
                  {(() => {
                    const rest = p.lost - (p.lostSameMonth ?? 0);
                    if (rest <= 0) return null;
                    const smLabelY = mid + h(p.lostSameMonth!) / 2 + 3;
                    const idealY = mid + h(p.lostSameMonth ?? 0) + h(rest) / 2 + 3;
                    return (
                      <text x={cx(i) + barW + 6} y={Math.max(idealY, smLabelY + 10)} fontSize="8.5"
                        fill={RED} textAnchor="start" fontWeight={750}>{rest}</text>
                    );
                  })()}
                </>
              )}
              <text x={cx(i) - barW / 2 - 2} y={mid - h(p.gained) - 5} fontSize="9.5" fill={ink.green}
                textAnchor="middle" fontWeight={750}>+{p.gained}</text>
              <text x={cx(i) + barW / 2 + 2} y={mid + h(p.lost) + 12} fontSize="9.5" fill={RED}
                textAnchor="middle" fontWeight={750}>−{p.lost}</text>
            </g>
          );
        })}
        {points.map((p, i) => (
          <text key={`x${i}`} x={cx(i)} y={H - 20} fontSize="10"
            fill={partial(i) ? AMBER : ink.strong} textAnchor="middle"
            fontWeight={hover === i || partial(i) || shownSel === p.month ? 750 : 500}>
            {fmtMonth(p.month)}{partial(i) ? ' ·' : ''}
          </text>
        ))}
        {/* net movement, the number that actually answers "are we growing?" */}
        {points.map((p, i) => (
          <text key={`n${i}`} x={cx(i)} y={H - 6} fontSize="9.5" textAnchor="middle" fontWeight={750}
            fill={p.net > 0 ? ink.greenFill : p.net < 0 ? RED : ink.strong}>
            {p.net > 0 ? `+${p.net}` : p.net}
          </text>
        ))}
        {/* [DRILLDOWN 2026-08-17] Spans the FULL column height, not just the plot area — the
            month name and the net figure sit below the old `y={mt} height={ph}` band, so
            clicking the label (the obvious target) used to do nothing. */}
        {points.map((p, i) => (
          <rect key={`h${i}`} x={ml + band * i} y={0} width={band} height={H}
            fill="transparent" style={{ cursor: onPick ? 'pointer' : 'default' }}
            onMouseEnter={() => setHover(i)}
            onPointerDown={onPick ? (e) => { monthPressedRef.current = isPlainMousePress(e); if (monthPressedRef.current) pickMonth(p.month); } : undefined}
            onClick={onPick ? (e) => { if (clickEndsPress(monthPressedRef, e)) return; pickMonth(p.month); } : undefined} />
        ))}
      </svg>
    </ChartScroll>
  );
};

/** Percent of a total, guarding the zero denominator the retention leak can legitimately have. */
const pctOf = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);

/* ── sales funnel ─────────────────────────────────────────────────────────────────
 *
 * [FUNNEL 2026-08-17] Deliberately NOT a chart. Two things pushed this to plain layout:
 *
 *  1. The page already spends horizontal bars on Market and vertical bars on Momentum and
 *     Client base. A third bar form would read as more of the same.
 *  2. The stages span 13,501 down to 370 — a 36x drop. Drawn proportionally, whether as a
 *     classic tapering funnel or as bars, the last stage is a sliver and the conversion rates
 *     (the actual content) become unreadable. A shape that misleads is worse than no shape.
 *
 * So the stages are tiles and the DROP-OFF sits between them, which is what anyone reads a
 * funnel for. No geometry to misinterpret.
 */
const FunnelRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 6px;
  flex-wrap: wrap;
`;

const FunnelStage = styled.div`
  flex: 1 1 0;
  min-width: 128px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.line};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(22,25,23,0.025)')};
`;

const FunnelLabel = styled.div`
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${inkText};
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const FunnelValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.4px;
  margin-top: 3px;
  color: ${(p) => p.theme.text};
`;

/* The gap between two stages. This is the number the card exists to show, so it is not a
   footnote — it sits at the same visual weight as the values it connects. */
const FunnelGap = styled.div<{ $tone: 'good' | 'warn' | 'bad' }>`
  flex: 0 0 auto;
  align-self: center;
  text-align: center;
  padding: 0 2px;
  font-size: 12.5px;
  font-weight: 800;
  white-space: nowrap;
  color: ${(p) => (p.$tone === 'good' ? p.theme.greenInk : p.$tone === 'warn' ? AMBER : RED)};

  span {
    display: block;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: ${inkText};
    margin-top: 1px;
  }

  @media (max-width: 760px) {
    align-self: flex-start;
    padding: 2px 0 6px 14px;
    text-align: left;
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: the drop-off figure at 13 px, its caption at 11 px. */
  @media (max-width: 768px) {
    font-size: 13px;

    span {
      font-size: 11px;
    }
  }
`;

/* [PROCESS PHONE FUNNEL SCROLL 2026-09-23, user] "make this scrollable horizontally … in the phone
   only", then "same for this" for the Retention Pipeline. Used by BOTH funnels on this page
   (Leads to clients, and Placed inventory → Expected → Retained / Cancelled); the shared
   FunnelRow / FunnelStage / FunnelGap stay as they were. On phones the steps wrapped onto several
   lines and the percentages landed in odd places. Here they stay ONE row in order and the row
   scrolls sideways: each tile sizes to its own content (so a label like "MEETINGS BOOKED" never
   spills past a 128px tile), and each gap figure is centred between the two steps it connects, as
   on desktop (undoing FunnelGap's wrapped-layout phone rule, inside this row only). */
const PhoneSwipeFunnelRow = styled(FunnelRow)`
  @media (max-width: 768px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 4px;
    overscroll-behavior-x: contain; /* [PHONE-LAYOUT 2026-10-22] the swipe never drags the page */
    -webkit-overflow-scrolling: touch;

    ${FunnelStage} {
      flex: 0 0 auto;
    }
    ${FunnelGap} {
      align-self: center;
      padding: 0 2px;
      text-align: center;
    }
  }
`;

/* ── client base drill-down modal ─────────────────────────────────────────────── */

const SheetTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 750;
  letter-spacing: -0.01em;
  /* [LIGHT-MODE 2026-08-17] theme.text is tuned for an opaque page surface; over the sheet's
     translucent glass it greys out. MappingModal solves this the same way — a hard slate. */
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#ffffff' : '#161917')};
`;

/* [LIGHT-MODE 2026-08-17] The table used to sit straight on the sheet's translucent glass, so in
   light mode the page's green and red bars showed through it and washed the rows out. This is the
   panel MappingModal (the Payments "Edit Mapping" sheet) puts its content on — an opaque-enough
   white surface that the glass stops bleeding through. th/td are targeted as elements rather
   than by interpolating Th/Td, because those are declared further down the file and referencing
   them here would blow up at module load. */
const TablePanel = styled.div`
  border-radius: 10px;
  overflow: hidden;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.62)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};

  th {
    padding: 11px 12px 9px;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.92)' : '#161917')};
    border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
  }

  td {
    color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.88)' : '#161917')};
    border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')};
  }

  tbody tr:last-child td { border-bottom: none; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: 7 px cell sides, so PM | New | Cancelled fit a 360 px phone (the Cancelled
     column was clipped by 15 px), and the panel swipes instead of clipping if a longer name still does not fit. */
  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;

    th { padding: 11px 7px 9px; }
    td { padding: 8px 7px; }
  }
`;

/* The whole PM row is the expander, so it has to read as pressable. */
const PmRow = styled.tr<{ $open: boolean }>`
  cursor: pointer;
  background: ${(p) => (p.$open
    ? (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.05)')
    : 'transparent')};

  &:hover {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(22,25,23,0.035)')};
  }
`;

const Caret = styled.i<{ $open: boolean }>`
  display: inline-block;
  width: 10px;
  margin-right: 7px;
  font-size: 9px;
  color: ${inkText};
  transform: rotate(${(p) => (p.$open ? 90 : 0)}deg);
  transition: transform ${duration.fast} ${easing.standard};
  ${reducedMotion} { transition: none; }
`;

/* The expanded client list. Two columns on desktop, one on a narrow sheet. Sunk slightly
   darker than the panel so it reads as nested under its PM rather than as more rows. */
const ClientGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 18px;
  /* [FLUID-8 2026-10-12] a PM's clients ripple in (soft, readable at once) when the row opens */
  ${fluidCascade('> *', 0, 12, 10)}
  padding: 10px 12px 14px 25px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.16)' : 'rgba(22,25,23,0.035)')};

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: a smaller indent, so the websites get the width. */
  @media (max-width: 768px) {
    padding: 10px 10px 12px 16px;
  }
`;

const ClientLine = styled.div<{ $tone: 'good' | 'bad' }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-size: 11px;
  padding: 2px 0;
  border-bottom: 1px dashed ${(p) => (p.theme.colorScheme === 'dark' ? p.theme.line : 'rgba(22,25,23,0.10)')};
  color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.88)' : '#161917')};

  b {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* [RED-LEAVERS 2026-08-31, user] "campaigns which left in red text" — but only the LEAVERS
       are coloured, deliberately: colouring the gainers green too made every row loud and
       nothing stand out. Red carries one meaning — this campaign is gone — and the stayers keep
       the neutral ink with their green + prefix. On dark, full RED sits near 3.5:1 at 11px, so
       the leaver ink is the lighter red the chart's divided bar already uses. */
    color: ${(p) => (p.$tone === 'bad'
    ? (p.theme.colorScheme === 'dark' ? '#e0705f' : RED)
    : 'inherit')};
  }
  b::before {
    content: '${(p) => (p.$tone === 'good' ? '+' : '−')}';
    color: ${(p) => (p.$tone === 'good' ? p.theme.greenInk : RED)};
    font-weight: 800;
    margin-right: 5px;
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: 12.5 px, and a long website wraps instead of losing its end; when the website
     and its type / status do not fit one line, the status drops to the next line at the right. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0 10px;
    font-size: 12.5px;
    padding: 4px 0;

    b {
      flex: 1 1 auto;
      min-width: 0;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      overflow-wrap: anywhere;
    }
  }
`;

const ClientStatus = styled.span<{ $odd: boolean }>`
  flex-shrink: 0;
  font-size: 9.5px;
  font-weight: ${(p) => (p.$odd ? 750 : 600)};
  letter-spacing: 0.2px;
  /* A campaign that started and was cancelled inside the same month is the case worth spotting,
     so it must not read like the rest. The amber is darkened in light mode — the dark-mode tone
     is barely legible on a pale panel. */
  color: ${(p) => {
    if (!p.$odd) return inkText(p); /* [FULL-INK 2026-09-01] was 55% white / slate-500 */
    return p.theme.colorScheme === 'dark' ? AMBER : '#a16207';
  }};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 11px;
    margin-left: auto;
  }
`;

const GroupLabel = styled.div`
  grid-column: 1 / -1;
  margin-top: 6px;
  font-size: 9.5px;
  font-weight: 750;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${inkText};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

/**
 * [REDEFINED 2026-08-17] Cancelled rows are now the loss definition itself, so every row in the
 * cancelled list reads `Cancelled` and highlighting that would say nothing. The interesting case
 * moved to the OTHER column: a campaign that started and was cancelled inside the SAME month —
 * 152 of them over Jan–Aug 2026. That is what gets flagged now.
 */
const startedAndEnded = (status: string | null) =>
  (status ?? '').replace(/\s/g, '').toLowerCase() === 'cancelled';

/* [MASTER-DETAIL 2026-08-27, user] The drilldown modal is GONE — "resize this chart and make
   this to the left side and on the right side i should have the table which i get when i click
   the bars". This is that right-side panel: the modal's content reparented onto a Card, same
   table, same rows, same client lists, so the figures cannot have changed. The sheet chrome
   went with it — no portal, no overlay, no Escape handling, and no `data-reload-block`: an
   inline panel simply re-renders through a deploy refresh instead of having to hold it off. */
const DetailHead = styled.div`
  padding: 0 0 10px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  margin-bottom: 10px;
`;

/* [DETAIL CLAMP 2026-09-01, user] The right panel used to cap its list at a fixed 430px, but
   a month with many PMs still stretched the grid row and the CHART card grew dead space to
   match ("when the data is more on the right side the left side also increase"). Now the LEFT
   card alone sets the row height: the right grid cell contributes nothing to row sizing
   (its card is absolutely positioned inside it), so the panel is exactly as tall as the chart
   card — summary line included — and everything past that scrolls inside DetailScroll. */
const DetailCell = styled.div`
  position: relative;
  min-height: 0;

  /* Single column: there is no left card beside it to take a height from, so the panel needs
     one of its own or the absolute fill collapses to nothing.
     [PHONE-LAYOUT 2026-10-22] Phones: no fixed 430 px box with its own scroller inside the page's scroll (a scroll
     trap on a touch screen, and the last PMs hid below its fold). The panel now takes its natural height: every PM
     row shows, and an opened PM's websites push the page down (see DetailCard / DetailScroll). */
  @media (max-width: 768px) {
    height: auto;
  }
`;

const DetailCard = styled(Card)`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* [PHONE-LAYOUT 2026-10-22] Phones: in the flow (see DetailCell). */
  @media (max-width: 768px) {
    position: relative;
    inset: auto;
  }
`;

const DetailScroll = styled.div`
  overflow-y: auto;
  /* Fills whatever the chart card's height leaves after the panel head; min-height 0 is what
     lets a flex child actually shrink and hand the rest to the scrollbar. */
  flex: 1 1 auto;
  min-height: 0;

  /* [PHONE-LAYOUT 2026-10-22] Phones: no inner scroller (see DetailCell). */
  @media (max-width: 768px) {
    overflow-y: visible;
  }
`;

const ClientBaseDetail: React.FC<{
  point: PaClientBasePoint;
}> = ({ point }) => {
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  // A month switch collapses the expansions. PM names repeat across months, so an expansion
  // held open would silently show a DIFFERENT month's clients under the same heading.
  useEffect(() => { setOpen(new Set()); }, [point.month]);

  // [FLUID-8 2026-10-12, user] A PM row opens / closes its clients on the mouse PRESS; touch / keyboard: the click.
  const rowPressedRef = useRef(false);
  const toggle = (pm: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(pm)) next.delete(pm); else next.add(pm);
    return next;
  });

  return (
    <>
      <DetailHead>
        <div>
            {/* [COPY 2026-08-31, user] The stats line (+N new · −N cancelled · net · live ·
                % cancelled) and the amber month-to-date caveat were both removed on request —
                the panel is the title and the per-PM table. */}
            <SheetTitle>{fmtMonthYear(point.month)} — new campaigns and cancellations</SheetTitle>
        </div>
      </DetailHead>
      <DetailScroll>
          {!point.byPm.length ? (
            <Empty>No new campaigns or cancellations in this month.</Empty>
          ) : (
            <TablePanel>
            <Table>
              <thead><tr>
                <Th>PM</Th><Th $right>New</Th><Th $right>Cancelled</Th>
              </tr></thead>
              <tbody>
                {point.byPm.map((r) => {
                  const isOpen = open.has(r.pm);
                  return (
                    <React.Fragment key={r.pm}>
                      <PmRow $open={isOpen} onPointerDown={(e) => { rowPressedRef.current = isPlainMousePress(e); if (rowPressedRef.current) toggle(r.pm); }} onClick={(e) => { if (clickEndsPress(rowPressedRef, e)) return; toggle(r.pm); }}>
                        <Td $strong>
                          <Caret className="fas fa-chevron-right" $open={isOpen} />
                          {/* [HUBSPOT NAMES 2026-08-17] Was `.replace(/[\s.]+$/, '')`, added on
                              the assumption that the trailing dot in "Anvesh ." was DB free-text.
                              It is not — HubSpot itself holds the name that way, and checking
                              confirmed every PM name in inventory matches the roster exactly. So
                              the trim was inventing a name nobody uses, and it made this sheet
                              disagree with the tenure card beside it, which never trimmed. */}
                          {r.pm}
                        </Td>
                        <Td $right>{r.gained ? <Pill $tone="good">+{r.gained}</Pill> : <Pill $tone="muted">0</Pill>}</Td>
                        <Td $right>{r.lost ? <Pill $tone="bad">−{r.lost}</Pill> : <Pill $tone="muted">0</Pill>}</Td>
                      </PmRow>
                      {isOpen && (
                        <tr>
                          <td colSpan={3} style={{ padding: 0 }}>
                            <ClientGrid>
                              {/* Cancelled rows all read `Cancelled` by definition, so the useful
                                  label here is the TYPE — it is what explains a month's split
                                  between recurring and one-time cancellations. */}
                              {/* [EXISTING-ONLY 2026-08-31, user] "right now we are seeing the
                                  same campaigns twice" — a second-month cancellation used to be
                                  listed here AND under New campaigns. It now appears ONCE,
                                  flagged, under New campaigns (where it began life), and this
                                  group is renamed to what it actually holds: cancellations of
                                  campaigns that were already on the book. The PM pill and the
                                  sheet header keep the FULL cancelled totals, so the label
                                  bridges the two counts. Filtering on `!c.sameMonth` also keeps
                                  a stale payload (no flag anywhere) rendering the old full list
                                  rather than an empty one. */}
                              {(() => {
                                const existing = r.lostClients.filter((c) => !c.sameMonth);
                                return (
                                  <>
                                    {/* [2026-08-31, user] Count only, no bridge text — "this
                                        will have the count of the campaigns and nothing more". */}
                                    {!!existing.length && (
                                      <GroupLabel>Existing Campaigns ({existing.length})</GroupLabel>
                                    )}
                                    {existing.map((c) => (
                                      <ClientLine key={`l${c.website}`} $tone="bad">
                                        <b>{c.website}</b>
                                        <ClientStatus $odd={false}>{c.type || 'no type'}</ClientStatus>
                                      </ClientLine>
                                    ))}
                                  </>
                                );
                              })()}
                              {/* [MINUS-FIRST 2026-08-31, user] Within New campaigns, the ones
                                  cancelled inside the month LEAD the list and carry the − prefix
                                  ("firstly it will show the cancelled one from the new campaigns
                                  and then the rest") — the sign says what happened to the money,
                                  the group says where the campaign came from. `sort` is stable,
                                  so the server's alphabetical order survives within each half. */}
                              {!!r.gainedClients.length && <GroupLabel>New campaigns ({r.gainedClients.length})</GroupLabel>}
                              {[...r.gainedClients]
                                .sort((a, b) => Number(startedAndEnded(b.status)) - Number(startedAndEnded(a.status)))
                                .map((c) => (
                                <ClientLine key={`g${c.website}`} $tone={startedAndEnded(c.status) ? 'bad' : 'good'}>
                                  <b>{c.website}</b>
                                  <ClientStatus $odd={startedAndEnded(c.status)}>
                                    {startedAndEnded(c.status)
                                      ? 'Second Month Cancellation'
                                      : `${c.type || 'no type'}${c.status ? ` · ${c.status}` : ''}`}
                                  </ClientStatus>
                                </ClientLine>
                              ))}
                            </ClientGrid>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
            </TablePanel>
          )}
      </DetailScroll>
    </>
  );
};

/* ── PM quadrant scatter ──────────────────────────────────────────────────────── */

const QuadrantChart: React.FC<{
  rows: PaPmQuadrantRow[]; medianRetention: number; medianUpsell: number;
}> = ({ rows, medianRetention, medianUpsell }) => {
  const ink = useInk();
  const [hover, setHover] = useState<number | null>(null);
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-22] the chart's phone width
  if (rows.length < 2) return <Empty>Not enough PMs with placed inventory to compare.</Empty>;

  /* [MATCH THE TABLE 2026-08-17] H was 330, which rendered ~395px beside a tenure table that now
     runs the full 17-PM roster at ~570px — the card was right, the chart just stopped short and
     left a band of empty space under it. 480 renders ~565px in this column, so the two cards read
     as one row again. The extra 150 user-units all land in the plot area (ph 268 -> 418), which
     also pulls apart the dots that used to pile up on the median line.

     ⚠ This is a PROPORTION, not a lock: the svg is width:100% over a fixed viewBox, so its height
     follows the COLUMN width, while the table's height follows the NUMBER OF PMs. They agree at
     today's 17. Add or lose several PMs and they will drift again — see the note in the section
     below about a row-per-PM chart, which would track the table structurally. */
  /* [PHONE-LAYOUT 2026-10-22] Phones: a scatter cannot be read by swiping through it like a bar chart (the first screen
     of a 912 px plot was empty: the PMs sit at 50-95 % retention), so on a phone it is drawn to fit the card: a
     340 x 400 plot with 12-unit text (about 11 px at 390), the median-retention label on the line's left when the line
     sits in the right half, and a larger tooltip. The dots keep the same scales as the laptop chart. */
  const W = isMobile ? 340 : 760, H = isMobile ? 400 : 480, ml = isMobile ? 30 : 52, mr = isMobile ? 12 : 18, mt = 20, mb = isMobile ? 36 : 42;
  const pw = W - ml - mr, ph = H - mt - mb;
  const maxRet = Math.max(...rows.map((r) => r.retentionPct), medianRetention, 1) * 1.08;
  const maxUps = Math.max(...rows.map((r) => r.upsell), medianUpsell, 1) * 1.12;
  const x = (v: number) => ml + (v / maxRet) * pw;
  const y = (v: number) => mt + ph - (v / maxUps) * ph;
  const colorFor = (q: PaPmQuadrantRow['quadrant']) =>
    q === 'Both strong' ? ink.greenFill : q === 'Retention only' ? TEAL : q === 'Upsell only' ? AMBER : RED;
  const flipMedLabel = isMobile && x(medianRetention) > W / 2; // [PHONE-LAYOUT 2026-10-22]

  const hp = hover != null ? rows[hover] : null;
  const tipW = isMobile ? 236 : 176, tipH = isMobile ? 66 : 56; // [PHONE-LAYOUT 2026-10-22] room for the phone text
  const tipX = hp ? Math.max(4, Math.min(W - tipW - 4, x(hp.retentionPct) - tipW / 2)) : 0;
  const tipY = hp ? (y(hp.upsell) - tipH - 12 < 4 ? y(hp.upsell) + 14 : y(hp.upsell) - tipH - 12) : 0;

  return (
    <ChartScroll>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: isMobile ? undefined : 580 /* [PHONE-LAYOUT 2026-10-22] fits the card */ }} onMouseLeave={() => setHover(null)}>
        {/* median crosshairs — the thresholds ARE the roster medians, so they are drawn, not implied */}
        <line x1={x(medianRetention)} y1={mt} x2={x(medianRetention)} y2={mt + ph}
          stroke={ink.axis} strokeDasharray="4 4" strokeWidth={1} opacity={0.7} />
        <line x1={ml} y1={y(medianUpsell)} x2={W - mr} y2={y(medianUpsell)}
          stroke={ink.axis} strokeDasharray="4 4" strokeWidth={1} opacity={0.7} />
        <text x={flipMedLabel ? x(medianRetention) - 5 : x(medianRetention) + 5} y={mt + 10} fontSize={isMobile ? 12 : 9}
          textAnchor={flipMedLabel ? 'end' : undefined} fill={ink.strong}>median retention {fmtPct(medianRetention, 0)}</text>
        <text x={ml + 4} y={y(medianUpsell) - 5} fontSize={isMobile ? 12 : 9} fill={ink.strong}>median upsell {fmtMoneyShort(medianUpsell)}</text>

        <line x1={ml} y1={mt + ph} x2={W - mr} y2={mt + ph} stroke={ink.axis} strokeWidth={0.75} />
        <line x1={ml} y1={mt} x2={ml} y2={mt + ph} stroke={ink.axis} strokeWidth={0.75} />

        {rows.map((r, i) => (
          <g key={r.pm} onMouseEnter={() => setHover(i)}
            style={{ transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hover == null || hover === i ? 1 : 0.4 }}>
            <circle cx={x(r.retentionPct)} cy={y(r.upsell)} r={hover === i ? 8 : 6}
              fill={colorFor(r.quadrant)} fillOpacity={0.75}
              stroke={colorFor(r.quadrant)} strokeWidth={1.5} style={{ transition: 'r 120ms cubic-bezier(0.4, 0, 0.2, 1)' }} />
          </g>
        ))}

        <text x={ml + pw / 2} y={H - 12} fontSize={isMobile ? 12 : 10.5} fill={ink.label} textAnchor="middle" fontWeight={650}>
          Retention % (collected ÷ placed inventory) →
        </text>
        <text x={14} y={mt + ph / 2} fontSize={isMobile ? 12 : 10.5} fill={ink.label} textAnchor="middle" fontWeight={650}
          transform={`rotate(-90, 14, ${mt + ph / 2})`}>Upsell $ →</text>

        <g transform={`translate(${tipX}, ${tipY})`} pointerEvents="none"
          style={{ transition: 'transform 120ms cubic-bezier(0.4, 0, 0.2, 1), opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hp ? 1 : 0 }}>
          <TipGlass w={tipW} h={tipH} dark={ink.dark} />
          {hp && (
            <>
              {/* [PHONE-LAYOUT 2026-10-22] phone sizes in the isMobile branches */}
              <text x={10} y={isMobile ? 19 : 16} fontSize={isMobile ? 12.5 : 10} fontWeight={750} fill={ink.strong}>{hp.pm}</text>
              <text x={10} y={isMobile ? 38 : 31} fontSize={isMobile ? 11.5 : 9} fill={ink.tipLabel}>Retention: <tspan fill={ink.strong} fontWeight={700}>{fmtPct(hp.retentionPct)}</tspan></text>
              <text x={10} y={isMobile ? 56 : 45} fontSize={isMobile ? 11.5 : 9} fill={ink.tipLabel}>Upsell: <tspan fill={ink.strong} fontWeight={700}>{fmtMoney(hp.upsell)}</tspan> · {hp.quadrant}</text>
            </>
          )}
        </g>
      </svg>
    </ChartScroll>
  );
};

/* ── compact table ────────────────────────────────────────────────────────────── */

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: data at 13 px. */
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

/* [PROCESS PM TENURE TABLE SCROLL 2026-09-23, user] "the table should have scroll, right now it
   comes out from the table". The "PMs whose clients stay longest" table sat straight in its Card,
   so on a phone its five nowrap columns ran past the card's right edge and pushed the whole page
   sideways. This box gives it its own sideways scroll. Inert when the table fits (full-width
   desktop looks the same). Used by that table only. */
const TableScrollX = styled.div`
  overflow-x: auto;
  max-width: 100%;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the table keeps its natural width (fills the box when it is narrower) and the
     swipe never drags the page. */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;

    & > table {
      width: max-content;
      min-width: 100%;
    }
  }
`;

/* [PHONE-LAYOUT 2026-10-22] The same sideways box for the four Sales-team tables, which sat straight in their cards: on
   a phone their columns ran past the card edge ("Still at M+2" and ATV cut off) and pushed the whole page card
   sideways. PhoneTableScroll renders this box on phones only; on a laptop it renders the table alone, as before. */
const PhoneTableBox = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;

    & > table {
      width: max-content;
      min-width: 100%;
    }
  }
`;

const PhoneTableScroll: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isMobile = useIsMobile();
  return isMobile ? <PhoneTableBox>{children}</PhoneTableBox> : <>{children}</>;
};


const Th = styled.th<{ $right?: boolean }>`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${inkText};
  padding: 0 8px 7px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px headers, 10 px side padding. */
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 0 10px 7px;
  }
`;

const Td = styled.td<{ $right?: boolean; $strong?: boolean }>`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  padding: 7px 8px;
  color: ${(p) => p.theme.text};
  font-weight: ${(p) => (p.$strong ? 700 : 500)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 190px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: no ellipsis on a name (the table swipes instead), 8 / 10 px padding. */
  @media (max-width: 768px) {
    padding: 8px 10px;
    max-width: none;
    overflow: visible;
    text-overflow: clip;
  }
`;

const Pill = styled.span<{ $tone: 'good' | 'warn' | 'bad' | 'muted' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 750;
  color: ${(p) => (p.$tone === 'good' ? p.theme.greenInk : p.$tone === 'warn' ? AMBER : p.$tone === 'bad' ? RED : inkText(p))};
  background: ${(p) => (p.$tone === 'good'
    ? p.theme.greenTint
    : p.$tone === 'warn' ? 'rgba(201,138,27,0.14)'
      : p.$tone === 'bad' ? 'rgba(192,57,43,0.12)' : 'transparent')};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 11.5px;
  }
`;

/* ── state × industry ─────────────────────────────────────────────────────────── */

const StateCard = styled(Card)`
  padding: 12px 14px 14px;
`;

const StateHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${(p) => p.theme.line};
`;

const StateName = styled.div`
  font-size: 13px;
  font-weight: 750;
  color: ${(p) => p.theme.text};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const StateTotal = styled.div`
  font-size: 12.5px;
  font-weight: 750;
  color: ${(p) => p.theme.greenInk};

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const StateRowLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 4px 0;
  font-size: 11.5px;

  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const StateInd = styled.span`
  color: ${(p) => p.theme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a long industry name wraps instead of losing its end. */
  @media (max-width: 768px) {
    min-width: 0;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    overflow-wrap: break-word;
  }
`;

const StateVal = styled.span`
  color: ${inkText};
  font-weight: 650;
  white-space: nowrap;
`;

/* ── skeleton ─────────────────────────────────────────────────────────────────── */

/* [SKELETON 2026-08-17] These were a bare grey rect in a Card, and several did not describe what
   actually loads: Geography drew THREE blocks against five real state cards (leaving two empty
   columns), Sales team drew two against its four real cards, and Client base drew a 250px block
   against a chart that renders ~476px. Each skeleton now mirrors its card — a title bar, a caption
   line, then a body sized like the real content.

   Chart bodies take an aspect RATIO rather than a pixel height. The charts are width:100% over a
   fixed viewBox, so their rendered height is (columnWidth x H/W) and tracks the screen — no fixed
   pixel value can be right at more than one width. Same reason the real charts vary. */
const SkBody = styled(SkeletonBox)<{ $ratio?: string }>`
  margin-top: 12px;
  ${(p) => (p.$ratio ? `height: auto; aspect-ratio: ${p.$ratio};` : '')}
`;

/* Sections whose real markup is two stacked grids with a footnote between them. */
const SkStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LoadingBlock: React.FC<{
  /** Body height in px. Ignored when `ratio` is given. */
  height?: number;
  /** `W/H` of the chart this stands in for, e.g. "760/260". Scales with the column. */
  ratio?: string;
  /** Card title + caption bars. Off for KPI tiles and state cards, which have neither. */
  head?: boolean;
  /** [PHONE-LAYOUT 2026-10-22] Phones only: the `W/H` of a chart that fits the card on a phone (the quadrant). */
  phoneRatio?: string;
}> = ({ height, ratio, head = true, phoneRatio }) => {
  /* [PHONE-LAYOUT 2026-10-22] On phones a swiping chart draws at its phone width (phoneChartWidth), not at the
     column's, so its height is the viewBox height x PHONE_CHART_SCALE: the chart skeleton takes that height, so nothing
     jumps when the chart lands. A chart that fits the card on a phone passes its phone ratio. Desktop keeps the ratio. */
  const isMobile = useIsMobile();
  const phoneH = isMobile && ratio && !phoneRatio ? Math.round(Number(ratio.split('/')[1]) * PHONE_CHART_SCALE) : null;
  const shownRatio = isMobile && phoneRatio ? phoneRatio : ratio;
  return (
  <Card>
    {head && (
      <>
        <SkeletonBox $w="54%" $h="13px" $radius="6px" />
        <SkeletonBox $w="86%" $h="10px" $radius="5px" $mt="7px" />
      </>
    )}
    <SkBody $h={phoneH ? `${phoneH}px` : height ? `${height}px` : undefined} $ratio={phoneH ? undefined : shownRatio} $radius="10px" />
  </Card>
  );
};

/* ── page ─────────────────────────────────────────────────────────────────────── */

const ProcessAnalysisPage: React.FC = () => {
  const isMobile = useIsMobile();
  /* [LIGHT-MODE BARS 2026-08-17] The two "total" legend swatches hardcoded a single rgba that
     matched neither theme's bar once the light value changed. Reading ink here keeps the swatch
     and the bar the same colour by construction, in both themes. */
  const ink = useInk();
  /** [MASTER-DETAIL 2026-08-27] The month the user CLICKED on the Client base chart, or null.
   *  This is the raw click only — what the panel actually shows is `clientBaseMonth` below. */
  const [pickedMonth, setPickedMonth] = useState<string | null>(null);
  // [TAB-FAST 2026-08-13] Seed the FIRST render straight from the SWR cache, and start with
  // loading already false when it hits. Both must be lazy initialisers: seeding only inside
  // load() is a tick too late — the first paint would still be a skeleton, and this is the
  // slowest payload in the app to rebuild, so that flash is exactly the one worth removing.
  const [data, setData] = useState<ProcessAnalysisData | null>(
    () => peekCache<ProcessAnalysisData>('/process-analysis') ?? null,
  );
  /* [MASTER-DETAIL 2026-08-27] The breakdown panel's month is DERIVED, never stored-and-corrected
     — the same rule as selectedKpiMonth. The user's click wins while that month exists in the
     payload; otherwise the RUNNING month (the user's chosen default); otherwise the last month.
     A background refresh that redraws the window can therefore never strand the panel. */
  const clientBaseMonth = useMemo(() => {
    const pts = data?.clientBase ?? [];
    if (!pts.length) return null;
    if (pickedMonth && pts.some((p) => p.month === pickedMonth)) return pickedMonth;
    const running = data?.window.inProgress?.month;
    if (running && pts.some((p) => p.month === running)) return running;
    return pts[pts.length - 1].month;
  }, [data, pickedMonth]);

  /* [MONEY COMPARE V2 2026-08-31] No pickers any more — the quarter and month charts show
     EVERYTHING, grouped (user: "it will have all the quarters ... all the months with the year
     comparison"). Derived wholesale from the server's monthly series. */
  const cmpView = useMemo(() => {
    const pts = data?.moneyCompare?.points ?? [];
    if (!data || !pts.length) return null;
    const yy = (m: string) => m.slice(0, 4);
    const mm = (m: string) => Number(m.slice(5, 7));
    const lastYm = data.window.months[data.window.months.length - 1];
    const running = data.window.inProgress?.month ?? null;
    const YEARS = ['2024', '2025', '2026'];

    const spanByKey = new Map<string, string>();
    const years: PaMoneyFlowPoint[] = [];
    for (const y of YEARS) {
      const own = pts.filter((p) => yy(p.month) === y);
      const sum = sumComparePoints(own, y);
      if (!sum) continue;
      spanByKey.set(y, own.length === 12 ? y : `${y} · ${fmtMonth(own[0].month)}–${fmtMonth(own[own.length - 1].month)}`);
      years.push(sum);
    }

    const quarterGroups = [1, 2, 3, 4].map((q) => ({
      label: `Quarter ${q}`,
      points: YEARS.map((y) => sumComparePoints(
        pts.filter((p) => yy(p.month) === y && Math.ceil(mm(p.month) / 3) === q), `Quarter ${q} ${y}`))
        .filter(Boolean) as PaMoneyFlowPoint[],
    })).filter((g) => g.points.length);
    const qPartialKey = running ? `Quarter ${Math.ceil(mm(running) / 3)} 2026` : null;

    const monthGroups = Array.from({ length: 12 }, (_, i) => i + 1).map((n) => ({
      label: MONTHS_FULL[n - 1],
      points: YEARS.map((y) => sumComparePoints(
        pts.filter((p) => yy(p.month) === y && mm(p.month) === n),
        `${MONTHS_FULL[n - 1]} ${y}`))
        .filter(Boolean) as PaMoneyFlowPoint[],
    })).filter((g) => g.points.length);
    const mPartialKey = running ? `${MONTHS_FULL[mm(running) - 1]} 2026` : null;

    const perYearMms = YEARS.map((y) => new Set(pts.filter((p) => yy(p.month) === y).map((p) => mm(p.month))))
      .filter((s) => s.size > 0);
    const common = [...(perYearMms[0] ?? new Set<number>())]
      .filter((n) => perYearMms.every((s) => s.has(n))).sort((a, b) => a - b);
    const likeForLike = common.length
      ? YEARS.map((y) => sumComparePoints(pts.filter((p) => yy(p.month) === y && common.includes(mm(p.month))), y))
        .filter(Boolean) as PaMoneyFlowPoint[]
      : [];
    const commonLabel = common.length
      ? `${fmtMonth(`2026-${String(common[0]).padStart(2, '0')}`)}–${fmtMonth(`2026-${String(common[common.length - 1]).padStart(2, '0')}`)}`
      : '';

    /* [COMPARE SUMMARY 2026-09-01, user] One computed line per compare card, in the same
       centred style as the rest. Year card: every complete year's net with the growth between
       the last two, then the running year "so far". Quarter/month cards share one rule: the
       latest COMPLETE period of the newest year against the same period a year earlier —
       the only comparison in those charts that is both like-for-like and current. */
    const partialYearKey = running ? '2026' : null;
    const doneYears = years.filter((p) => p.month !== partialYearKey);
    const runYear = years.find((p) => p.month === partialYearKey) ?? null;
    let yearSummary: string | null = null;
    if (years.length >= 2) {
      const parts = doneYears.map((p) => `${fmtMoneyShort(p.net)} in ${p.month}`);
      let growth = '';
      if (doneYears.length >= 2) {
        const a = doneYears[doneYears.length - 2].net;
        const b = doneYears[doneYears.length - 1].net;
        if (a > 0) growth = ` (${Math.abs(((b - a) / a) * 100).toFixed(0)}% ${b >= a ? 'up' : 'down'})`;
      }
      yearSummary = `Money kept: ${parts.join(', ')}${growth}${runYear
        ? ` — ${runYear.month} stands at ${fmtMoneyShort(runYear.net)} so far` : ''}.`;
    }

    const likeForLikeLine = (
      groups: { points: PaMoneyFlowPoint[] }[],
      partialKey: string | null,
    ): string | null => {
      const newest = YEARS.filter((y) => pts.some((p) => yy(p.month) === y)).pop();
      if (!newest) return null;
      const prevYr = String(Number(newest) - 1);
      let latest: PaMoneyFlowPoint | null = null;
      let twin: PaMoneyFlowPoint | null = null;
      for (const g of groups) {
        const cur = g.points.find((p) => p.month.endsWith(newest) && p.month !== partialKey);
        if (cur) {
          latest = cur;
          twin = g.points.find((p) => p.month.endsWith(prevYr)) ?? null;
        }
      }
      if (!latest) return null;
      const vs = twin && twin.net > 0
        ? ` — ${Math.abs(((latest.net - twin.net) / twin.net) * 100).toFixed(0)}% ${latest.net >= twin.net ? 'up' : 'down'} on ${twin.month}`
        : '';
      const run = partialKey
        ? groups.flatMap((g) => g.points).find((p) => p.month === partialKey) ?? null : null;
      return `${latest.month} kept ${fmtMoneyShort(latest.net)}${vs}${run
        ? `; ${run.month} stands at ${fmtMoneyShort(run.net)} so far` : ''}.`;
    };
    const quarterSummary = likeForLikeLine(quarterGroups, qPartialKey);
    const monthSummary = likeForLikeLine(monthGroups, mPartialKey);

    return {
      years, quarterGroups, monthGroups, spanByKey, running,
      qPartialKey, mPartialKey, likeForLike, commonLabel,
      yPartialLabel: `Jan – ${fmtMonth(lastYm)} so far`,
      yearSummary, quarterSummary, monthSummary,
    };
  }, [data]);
  const [loading, setLoading] = useState(() => !peekCache<ProcessAnalysisData>('/process-analysis'));
  const [error, setError] = useState<string | null>(null);

  /**
   * `silent` = a live refresh rather than the first load: keep the current figures on screen
   * and swap them when the new ones arrive. Showing the skeleton here would make the whole
   * page flash every time anyone anywhere edited a payment, which is worse than a figure
   * being a few seconds old. A silent failure is also swallowed rather than replacing a
   * working dashboard with an error — the next tick retries.
   */
  const load = useCallback(async (silent = false) => {
    // [TAB-FAST 2026-08-13] A warm cache means real figures are already on screen (seeded
    // above, or left from a previous visit) — revalidate behind them instead of throwing the
    // page back to a skeleton. A miss behaves exactly as before.
    const seed = silent ? undefined : peekCache<ProcessAnalysisData>('/process-analysis');
    if (!silent) {
      if (seed) { setData(seed); setLoading(false); } else setLoading(true);
      setError(null);
    }
    try {
      setData(await fetchProcessAnalysis());
    } catch (e: any) {
      if (silent) return;
      setError(e?.response?.data?.error === 'section_forbidden'
        ? 'You do not have access to Process Analysis.'
        : 'Failed to load Process Analysis.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // [LIVE 2026-07-31] Refetch whenever any source dataset changes. The shared ~4s
  // /api/data-version poll drives this for the whole app, so subscribing costs no extra
  // polling — and it catches writes from ANY source (a UI edit, the Stripe/PayPal sync, the
  // reconciler, a script), not just ones made in this tab.
  useRealtime(['inventory', 'payments', 'eom'], () => load(true));

  const windowLabel = useMemo(() => {
    if (!data) return '';
    const { months } = data.window;
    if (!months.length) return '';
    return `${fmtMonth(months[0])} – ${fmtMonthYear(months[months.length - 1])}`;
  }, [data]);

  /* ── headline month filter ──────────────────────────────────────────────────
     Scoped to the headline section ONLY (user's instruction: "the headline will work
     based on the month filter, rest all part will work for all the months combined").
     It is a pure selection over data already in the payload — every KPI card ships all
     of its months — so switching months costs no request, and in particular no extra
     HubSpot search, which is the thing that rate-limits and silently zeroes this tab. */
  const [kpiMonth, setKpiMonth] = useState('');

  /** Jan-2026 → the effective current month, newest first. Exactly the tab's own window. */
  const kpiMonthOptions = useMemo(
    () => (data ? [...data.window.months].reverse() : []),
    [data],
  );

  /** Land on the last month that actually FINISHED — see kpiForMonth for why. */
  const defaultKpiMonth = useMemo(() => {
    if (!data) return '';
    const ip = data.window.inProgress;
    const complete = ip ? data.window.months.filter((m) => m !== ip.month) : data.window.months;
    if (complete.length) return complete[complete.length - 1];
    // Nothing has closed yet (a window one month long). Fall back to the running month, which
    // then reports as month-to-date with no delta rather than as a headline figure.
    return ip ? ip.month : '';
  }, [data]);

  /* Derived rather than stored: a refetch that moves the window (month rollover) can never
     strand the section on a month that no longer exists, and correcting it needs no effect
     — which would otherwise render once with the stale month before fixing itself. */
  const selectedKpiMonth = useMemo(
    () => (kpiMonth && data?.window.months.includes(kpiMonth) ? kpiMonth : defaultKpiMonth),
    [kpiMonth, data, defaultKpiMonth],
  );

  /* [OWN PILLS 2026-08-31, user] The central MONTH dropdown is GONE — each month-scoped section
     now carries its own pill picker: the headline's drives the three KPI cards, and the Sales
     process and Retention pipeline sections each hold their OWN month ("it will have its own
     month filter in the pill"), so the three can sit on different months side by side. Same
     derived-not-stored pattern as kpiMonth, same default landing month. */
  const [funnelMonth, setFunnelMonth] = useState('');
  const selectedFunnelMonth = useMemo(
    () => (funnelMonth && data?.window.months.includes(funnelMonth) ? funnelMonth : defaultKpiMonth),
    [funnelMonth, data, defaultKpiMonth],
  );
  const [retentionMonth, setRetentionMonth] = useState('');
  const selectedRetentionMonth = useMemo(
    () => (retentionMonth && data?.window.months.includes(retentionMonth) ? retentionMonth : defaultKpiMonth),
    [retentionMonth, data, defaultKpiMonth],
  );

  // [FLUID-6 2026-10-12, user] "process analysis is not having the animation". When the figures land (a tab visit,
  // after the ~1 s skeleton; or a retry) every table row on screen rises in, top to bottom across the page, as ONE
  // wave (components/Fluid.tsx), and each section's cards rise in under their heads. Then each control replays only
  // its own part: the headline month pick → the four KPI tiles; the Sales process / Retention pipeline month picks →
  // that section's card; a month clicked on the Client base chart → the breakdown table's rows. The live (silent)
  // refresh keeps every key as it was, so it never replays anything.
  const landed = !loading && !error && !!data;
  const pageRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  useRowsLand(detailRef, landed ? clientBaseMonth : null);
  useRowsLand(pageRef, landed ? 'land' : null); // after the line above: on a landing the page's one wave wins
  const pageLand = useLandWindow(landed, 'land');
  const kpiLand = useLandWindow(landed, selectedKpiMonth);
  const funnelLand = useLandWindow(landed, selectedFunnelMonth);
  const retentionLand = useLandWindow(landed, selectedRetentionMonth);

  /** The one option list all three pill pickers share — full month names, running month marked. */
  const monthOptionNodes = kpiMonthOptions.map((m) => (
    <option key={m} value={m}>
      {fmtMonthYearFull(m)}{data?.window.inProgress?.month === m ? ' · so far' : ''}
    </option>
  ));

  // One caption for every chart that ends on the running month, so "the last bar is short
  // because the month just started" is stated rather than left to be inferred.
  const mtdLegend = useMemo(() => {
    const ip = data?.window.inProgress;
    return ip ? `${fmtMonthYear(ip.month)} — month to date, day ${ip.day} of ${ip.daysInMonth}` : '';
  }, [data]);

  const salesCombo: ComboPoint[] = useMemo(
    () => (data?.salesTrend ?? []).map((p: PaTrendPoint) => ({
      month: p.month,
      bar: p.total,
      line: p.recurring,
      // [NET SALES IN BANK 2026-09-17] The bar is the New Sales tab's Total New Sales = Recurring +
      // One-Time + Sales PIA + recovery (ROW new sales inside recurring/one-time). Recurring is the
      // line, so the tooltip spells out the rest rather than leaving the total unaccounted for.
      extra: [
        { label: 'One-time', value: fmtMoney(p.oneTime) },
        { label: 'Sales PIA', value: fmtMoney(p.salesPia) },
        { label: 'Recovery', value: fmtMoney(p.recovery) },
      ],
    })),
    [data],
  );
  const inventoryCombo: ComboPoint[] = useMemo(
    () => (data?.inventoryTrend ?? []).map((p: PaInventoryTrendPoint) => ({
      month: p.month,
      bar: p.placedInventory,
      line: p.retentionPct,
      // Current Retention is Placed in Bank ÷ Placed Inventory, so showing the bank figure
      // makes the percentage on the line self-explanatory rather than something to take on trust.
      extra: [{ label: 'Placed in bank', value: fmtMoney(p.placedInBank) }],
    })),
    [data],
  );

  /* [TREND SUMMARY 2026-09-01, user] The two computed lines under each chart's legend. */
  const runningMonth = data?.window.inProgress?.month ?? null;
  const salesSummary = useMemo(
    () => comboTrendLines(salesCombo, runningMonth, 'New sales', true, 'brought in',
      fmtMoneyShort, (p) => `Recurring made up ${fmtMoneyShort(p.line)} of it.`),
    [salesCombo, runningMonth],
  );
  const inventorySummary = useMemo(
    () => comboTrendLines(inventoryCombo, runningMonth, 'Placed inventory', false, 'placed',
      fmtMoneyShort, (p) => `Current retention stands at ${fmtPct(p.line, 0)}.`),
    [inventoryCombo, runningMonth],
  );
  /* [MONEY-FLOW SUMMARY 2026-09-01, user] Same computed treatment for the big money-flow
     chart — the trend is judged on NET (the dot, the card's headline figure), and the last
     month's line closes with gross collected and what went back out. */
  const moneyFlowSummary = useMemo(() => {
    const flow = data?.moneyFlow ?? [];
    const byMonth = new Map(flow.map((p) => [p.month, p]));
    return comboTrendLines(
      flow.map((p) => ({ month: p.month, bar: p.net, line: p.gross })),
      runningMonth, 'Money kept', false, 'kept', fmtMoneyShort,
      (last) => {
        const src = byMonth.get(last.month);
        return src
          ? `Of ${fmtMoneyShort(src.gross)} collected, ${fmtMoneyShort(src.refundsDisputes)} went back out.`
          : '';
      },
    );
  }, [data, runningMonth]);

  const industryBars = (rows: PaIndustryRow[], mode: 'inbank' | 'atv'): BarItem[] => rows.map((r) => ({
    name: r.industry,
    value: mode === 'inbank' ? r.inBank : r.atv,
    primary: mode === 'inbank' ? fmtMoney(r.inBank) : fmtMoney(r.atv),
    meta: `${fmtInt(r.clients)} client${r.clients === 1 ? '' : 's'}`,
  }));

  // [LOAD-STATES 2026-09-29, user] A plain line with no retry → the countdown card. "No access" stays
  // a plain message (a final answer, not a failed load).
  if (error) {
    return (
      <Container>
        {error.startsWith('You do not have access')
          ? <ErrorBox>{error}</ErrorBox>
          : <LoadFailed what="Process Analysis" onRetry={() => load()} />}
      </Container>
    );
  }

  /* [COHORT ROWS 2026-08-17] Was the top 6 and the bottom 6 REVERSED, which hid the middle of the
     roster entirely — 12 of today's 24 qualifying reps appeared on neither card — and left the
     two tables running in opposite directions.

     It is now one ranked list, split in half, BOTH halves sorted high-to-low on M+2 (the server
     already returns it that way, so the bottom half simply keeps its order rather than being
     reversed). Every rep appears exactly once, on exactly one card, and reading the churn card
     straight after the stay card continues the same ranking. Each list box shows ~10 rows and
     scrolls for the remainder. */
  const cohort = data?.salesReps.cohort ?? [];
  const cohortHalf = Math.ceil(cohort.length / 2);
  const cohortTop = cohort.slice(0, cohortHalf);
  const cohortBottom = cohort.slice(cohortHalf);

  /* [COHORT SUMMARY 2026-09-01, user] One computed line under each cohort card. The share is
     weighted by clients won — "of the clients this half won, X% still pay" — because a plain
     mean of the per-rep percentages would let a 5-client 100% outweigh a 34-client 65%. The
     named rep is the list's own top (or bottom) row, ties stated as ties. */
  const cohortLine = (
    rows: Array<{ rep: string; wonClients: number; m2Pct: number }>,
    best: boolean,
  ): string | null => {
    if (rows.length < 2) return null;
    const clients = rows.reduce((s, r) => s + r.wonClients, 0);
    if (clients <= 0) return null;
    const pct = rows.reduce((s, r) => s + (r.m2Pct / 100) * r.wonClients, 0) / clients * 100;
    const lead = best
      ? (() => {
        const ties = rows.filter((r) => r.m2Pct === rows[0].m2Pct);
        return ties.length > 1
          ? `${ties.length} reps tie at the top on ${fmtPct(rows[0].m2Pct, 0)}`
          : `${rows[0].rep} leads at ${fmtPct(rows[0].m2Pct, 0)}`;
      })()
      : `${rows[rows.length - 1].rep} sits lowest at ${fmtPct(rows[rows.length - 1].m2Pct, 0)}`;
    return `Of the ${fmtInt(clients)} clients this half won, ${fmtPct(pct, 0)} still pay at two months — ${lead}.`;
  };
  const cohortTopLine = cohortLine(cohortTop, true);
  const cohortBottomLine = cohortLine(cohortBottom, false);

  /* [RANK SUMMARY 2026-09-01, user] Same treatment for the money pair: each half's total, its
     share of everything the team brought in, and the edge of the list (leader on the top card,
     lowest on the bottom one). The share needs both halves, so it is derived here once. */
  const rankTopRows = data?.salesReps.topHalf ?? [];
  const rankBottomRows = data?.salesReps.bottomHalf ?? [];
  const rankAllTotal = [...rankTopRows, ...rankBottomRows].reduce((s, r) => s + r.inBank, 0);
  const rankLine = (rows: Array<{ rep: string; inBank: number }>, best: boolean): string | null => {
    if (rows.length < 2 || rankAllTotal <= 0) return null;
    const total = rows.reduce((s, r) => s + r.inBank, 0);
    const share = (total / rankAllTotal) * 100;
    const edge = best
      ? `${rows[0].rep} leads at ${fmtMoneyShort(rows[0].inBank)}`
      : `${rows[rows.length - 1].rep} sits lowest at ${fmtMoneyShort(rows[rows.length - 1].inBank)}`;
    return `Between them ${fmtMoneyShort(total)} — ${share.toFixed(0)}% of everything the team brought in; ${edge}.`;
  };
  const rankTopLine = rankLine(rankTopRows, true);
  const rankBottomLine = rankLine(rankBottomRows, false);

  /* [PM SUMMARY 2026-09-01, user] One computed line under each PM card. Tenure is weighted by
     clients held — 2 clients at 1.0 months must not pull on the average like 128 at 3.9. The
     quadrant counts reuse the rows' own `quadrant` field, never a second median comparison. */
  const tenureRows = data?.pms.tenure ?? [];
  const tenureLine = (() => {
    if (tenureRows.length < 2) return null;
    const clients = tenureRows.reduce((s, r) => s + r.clients, 0);
    if (clients <= 0) return null;
    const avg = tenureRows.reduce((s, r) => s + r.avgMonths * r.clients, 0) / clients;
    const top = tenureRows.reduce((a, b) => (b.avgMonths > a.avgMonths ? b : a));
    return `Across ${fmtInt(tenureRows.length)} PMs and ${fmtInt(clients)} client relationships, a client stays ${fmtNum1(avg)} months on average — ${top.pm} holds them longest at ${fmtNum1(top.avgMonths)}.`;
  })();
  /* [CLIENT-BASE SUMMARY 2026-09-01, user] Replaces the methodology footnote under the chart
     ("a campaign is new when…", month-end stamping) with a computed summary: the window's
     totals, then the last complete month. The running month stays out — its cancellations are
     stamped at month-end, so counting it would report a month with almost no losses. */
  const cbComplete = (data?.clientBase ?? []).filter((p) => p.month !== runningMonth);
  const clientBaseLine = (() => {
    if (cbComplete.length < 2) return null;
    const g = cbComplete.reduce((s, p) => s + p.gained, 0);
    const l = cbComplete.reduce((s, p) => s + p.lost, 0);
    const last = cbComplete[cbComplete.length - 1];
    const sign = (n: number) => (n > 0 ? `+${fmtInt(n)}` : fmtInt(n));
    return `Since ${fmtMonth(cbComplete[0].month)}: ${fmtInt(g)} campaigns started against ${fmtInt(l)} cancelled, net ${sign(g - l)} — ${fmtMonthYear(last.month)} added ${fmtInt(last.gained)}, lost ${fmtInt(last.lost)}, net ${sign(last.net)}.`;
  })();

  const quadRows = data?.pms.quadrant ?? [];
  const quadLine = (() => {
    if (quadRows.length < 2 || !data) return null;
    const strong = quadRows.filter((r) => r.quadrant === 'Both strong').length;
    const weak = quadRows.filter((r) => r.quadrant === 'Both weak').length;
    return `${fmtInt(strong)} of ${fmtInt(quadRows.length)} PMs are strong at both, ${fmtInt(weak)} below the middle on both — the team's middle is ${fmtPct(data.pms.medianRetentionPct, 0)} retention and ${fmtMoneyShort(data.pms.medianUpsell)} upsell.`;
  })();

  // [MONTH CLARITY 2026-08-15] The two windows the Sales-team cards actually cover. Derived
  // from the payload, never hardcoded: the cohort end moves with the calendar and the
  // scoreboard window rolls every month. Empty string when absent, so the pill is omitted
  // rather than rendering an empty badge.
  const cohortRange = monthRangeLabel(data?.salesReps.cohortMonths ?? []);
  const scoreboardRange = monthRangeLabel(data?.salesReps.scoreboardWindow ?? []);

  return (
    <Container ref={pageRef /* [FLUID-6 2026-10-12] the page's row wave */}>
      <PageHead>
        <TitleLine>
          <PageTitle>Process Analysis</PageTitle>
          <TitleSep>|</TitleSep>
          <PageSub>January 2026 onwards</PageSub>
        </TitleLine>
        {/* [PILL HINT 2026-09-01, user] Every chart names the period it covers, but nothing said
            which of those the reader could CHANGE — amber ones are pickers, grey ones are fixed.
            One line, said once here rather than on every section.

            [2026-09-01, user] The page's "Jan – Sep 2026" WindowPill was removed from beside it:
            every section now states its own period on its own head, so the page-level pill was
            repeating what the sections already say. This line takes the right of the head alone,
            pushed there by PageHead's space-between — no wrapper needed. */}
        <PillHint>
          Every chart states the month it covers — amber you can change, the rest are fixed.
        </PillHint>
      </PageHead>

      {/* ── 1. headline ───────────────────────────────────────────────────── */}
      <Section>
        {/* [ONE-LINE HEADLINE 2026-09-01, user — TEST, may be reverted] Eyebrow, title, measure
            line and month filter on ONE row, pipe-separated. The previous two-row arrangement is
            saved verbatim in the session scratchpad (headline-original.txt) for an exact revert. */}
        <SectionHead>
          <OneLineHead>
            <Eyebrow>The headline</Eyebrow>
            <TitleSep>|</TitleSep>
            {/* [COPY 2026-09-01, user] "against the 2026 run-rate" was jargon — plain words now,
                and each card's own note still names the exact months behind the average ("vs $X
                monthly average from Jan – Jun 2026"). Yearless on purpose, so it stays true when
                the window crosses into 2027. */}
            <SectionTitle>
              How {selectedKpiMonth ? fmtMonthYear(selectedKpiMonth) : 'the last complete month'}
              {' '}compares with the months before it
            </SectionTitle>
            <TitleSep>|</TitleSep>
            {/* [COPY 2026-09-01, user] One line naming what the four cards measure — a different
                sentence from the 2026-08-31 explainer that was removed: that one explained the
                comparison, this one names the measures. */}
            <OneLineNote>
              Leads coming in, money from new sales, money kept from existing clients, and the
              month&#39;s net — each against the earlier months&#39; average.
            </OneLineNote>
            {/* [OWN PILLS 2026-08-31, user] The headline's pill IS the month picker for the KPI
                cards — the separate MONTH dropdown that used to sit to the right was removed as
                redundant once the pill could do its job. [FAR RIGHT 2026-09-01, user] Parked at
                the row's right edge with no pipe before it — see OneLineRight. */}
            {!loading && data && selectedKpiMonth && (
              <OneLineRight>
                <PillSelect liquid
                  value={selectedKpiMonth}
                  deferChange /* [FLUID-8 2026-10-12] instant pick */ onChange={(e) => setKpiMonth(e.target.value)}
                  title="Pick the month the cards below compare."
                >
                  {monthOptionNodes}
                </PillSelect>
              </OneLineRight>
            )}
          </OneLineHead>
        </SectionHead>
        {loading || !data ? (
          /* KPI tiles carry no card title or caption — label, value, sparkline, sub.
             [EOM KPI 2026-09-01] Four now — the skeleton must match the real grid, or the tiles
             visibly jump a column when the data lands. */
          <KpiSwipe $cols={4} /* [PHONE-LAYOUT 2026-10-22] a Grid on a laptop, the phone swipe row on phones */>
            {/* [PHONE-LAYOUT 2026-10-22] phones: as tall as a real tile in the swipe row (about 232 px) */}
            <LoadingBlock height={isMobile ? 192 : 128} head={false} />
            <LoadingBlock height={isMobile ? 192 : 128} head={false} />
            <LoadingBlock height={isMobile ? 192 : 128} head={false} />
            <LoadingBlock height={isMobile ? 192 : 128} head={false} />
          </KpiSwipe>
        ) : (
          <Reveal $plain $rows /* [FLUID-4 2026-10-12] first rows rise in as the data lands */>
            {/* [MOTION-A 2026-08-20] Fades the real content in as it replaces the skeleton. */}
          <KpiSwipe $cols={4} $land={kpiLand /* [FLUID-6 2026-10-12] the tiles rise in on a landing / month pick */} /* [PHONE-LAYOUT 2026-10-22] a Grid on a laptop */>
            {/* [KPI-CAPTION 2026-08-31, user] "a single line data which explains the kpi
                perfectly in a simple manner and in simple english" — each caption is one
                plain sentence, no formulas or internal terms. */}
            <KpiTile
              label="Daily average leads" sub="How many new leads the sales team gets to work on in a typical working day."
              card={data.kpis.dailyLeads} month={selectedKpiMonth}
              format={fmtNum1} color={TEAL} />
            {/* [NET SALES IN BANK 2026-09-17, user] "make this kpi Sales in bank as net Sales in bank".
                The value is the New Sales tab's Net In-Bank for the month, to the cent. */}
            <KpiTile
              label="Net Sales in bank" sub="What new client sales brought into the bank, minus refunds and disputes, plus disputes won."
              card={data.kpis.salesInBank} month={selectedKpiMonth}
              format={fmtMoney} color={ink.greenFill} />
            {/* [MATCH MY INVENTORY 2026-08-15] This card carries My Inventory's Current Net
                In-Bank figure, so the caption must still state its real composition (renewals +
                upsells, net of refunds/disputes) — otherwise the label promises a smaller number. */}
            <KpiTile
              label="PM Net In - Bank" /* [2026-08-27, user] their exact wording */
              /* [UPSELL-HEALTH 2026-08-24] This card INCLUDES upsell, so a month HubSpot could
                 not be read for is understated by an unknown amount. Name those months rather
                 than let the series show a dip that never happened. */
              /* [COPY 2026-09-01, user] Reworded into one plain sentence, same pass as the
                 headline — the composition still names all four parts. */
              sub={data.upsellFailedMonths?.length
                ? `What existing clients paid in — retention, upsells and disputes won — minus refunds and disputes. ⚠ Upsell is missing for ${data.upsellFailedMonths.join(', ')}, so those months show less than reality.`
                : 'What existing clients paid in — retention, upsells and disputes won — minus refunds and disputes.'}
              card={data.kpis.pmInBank} month={selectedKpiMonth}
              format={fmtMoney} color={ink.greenFill} />
            {/* [EOM KPI 2026-09-01, user] "add the 4th card for the eom data, the same way
                revenue engine gets the data" — it IS the Revenue engine's series (server-side
                `moneyFlow`, one array feeding both), so this card and the chart lower down can
                never disagree. Since [PIA IN GROSS 2026-09-01] that number is also the EOM tab's
                Net SEO Total. Optional-guarded so an older server payload just shows three. */}
            {data.kpis.eom && (
              <KpiTile
                label="Net EOM" /* [2026-09-01, user] their wording; KpiLabel uppercases it */
                sub="Everything collected this month, minus refunds and lost disputes, plus money won back — the EOM tab's Net SEO Total."
                card={data.kpis.eom} month={selectedKpiMonth}
                format={fmtMoney} color={ink.greenFill} />
            )}
          </KpiSwipe>
          </Reveal>
        )}
      </Section>

      {/* ── 2. sales process ──────────────────────────────────────────────── */}
      {/* [FUNNEL PER MONTH 2026-08-17 → OWN PILL 2026-08-31] Month-by-month rather than one
          total for the whole activity range — a funnel you can step through shows whether a
          stage is DEGRADING, which a single aggregate never can. Since 2026-08-31 the section
          holds its OWN month (the pill on the title), independent of the headline's.
          ⚠ The activity table starts 2026-03, so picking January or February finds no month and
          the card says so rather than drawing zeros. */}
      {(loading || data?.funnel.length) ? (
        <Section $land={funnelLand /* [FLUID-6 2026-10-12] landing + this section's month pick */}>
          {/* [ONE-LINE HEAD 2026-09-01, user — TEST] Same single-row arrangement as the headline:
              eyebrow | title | measure line | month filter. See OneLineHead. */}
          <SectionHead>
            <OneLineHead>
              <Eyebrow>Sales process</Eyebrow>
              <TitleSep>|</TitleSep>
              {/* [COPY 2026-09-01, user] Plain-English pass — the title names the journey, the
                  note lists the four steps in the order the funnel draws them. */}
              <SectionTitle>How leads become clients</SectionTitle>
              <TitleSep>|</TitleSep>
              <OneLineNote>
                Leads assigned, meetings booked, meetings held, clients won — and how many made it
                from each step to the next.
              </OneLineNote>
              {/* [FAR RIGHT 2026-09-01, user] Right edge, no pipe — see OneLineRight. */}
              {!loading && data && selectedFunnelMonth && (
                <OneLineRight>
                  <PillSelect liquid
                    value={selectedFunnelMonth}
                    deferChange /* [FLUID-8 2026-10-12] instant pick */ onChange={(e) => setFunnelMonth(e.target.value)}
                    title="Pick the month this section shows — independent of the other sections."
                  >
                    {monthOptionNodes}
                  </PillSelect>
                </OneLineRight>
              )}
            </OneLineHead>
          </SectionHead>
          {loading ? <LoadingBlock height={120} /> : (() => {
            const f = data?.funnel.find((x) => x.month === selectedFunnelMonth) ?? null;
            if (!f) {
              return (
                <Card>
                  <Empty>
                    No meetings or calls recorded for {fmtMonthYear(selectedFunnelMonth)} — this data
                    begins in {data?.funnel.length ? fmtMonthYear(data.funnel[0].month) : 'a later month'}.
                  </Empty>
                </Card>
              );
            }
            return (
              <Card>
                <CardTitle>Leads to clients, {fmtMonthYear(f.month)}</CardTitle>
                <PhoneSwipeFunnelRow>
                  {f.steps.map((s, i) => (
                    <React.Fragment key={s.label}>
                      {i > 0 && s.pctOfPrev !== null && (
                        /* Thresholds describe the drop, not a target: over half carrying through
                           is healthy, a quarter is worth a look, below that is the leak. */
                        <FunnelGap $tone={s.pctOfPrev >= 50 ? 'good' : s.pctOfPrev >= 25 ? 'warn' : 'bad'}>
                          {fmtPct(s.pctOfPrev, 0)}
                          <span>carried through</span>
                        </FunnelGap>
                      )}
                      <FunnelStage>
                        <FunnelLabel>{s.label}</FunnelLabel>
                        <FunnelValue>{fmtInt(s.value)}</FunnelValue>
                      </FunnelStage>
                    </React.Fragment>
                  ))}
                </PhoneSwipeFunnelRow>
                {/* [COPY 2026-08-17] Foot note removed at the user's request. The calls, talk-time
                    and no-show figures are still on the payload (`calls`, `talkHours`, `noShows`)
                    if they are ever wanted back — nothing server-side was torn out for this. */}
              </Card>
            );
          })()}
        </Section>
      ) : null}

      {/* ── 3. sales people ───────────────────────────────────────────────── */}
      <Section $land={pageLand /* [FLUID-6 2026-10-12] */}>
        <SectionHead>
          {/* [COPY 2026-09-01, user] The note used to restate the title; now it maps the two
              pairs of cards below — cohort pair on top, money pair beneath. */}
          <OneLineHead>
            <Eyebrow>Sales team</Eyebrow>
            <TitleSep>|</TitleSep>
            <SectionTitle>Whose clients stay, and who brings in the most</SectionTitle>
            <TitleSep>|</TitleSep>
            <OneLineNote>
              Top: whose clients still pay two months on · Bottom: the team by money brought in.
              Each card shows its own dates.
            </OneLineNote>
          </OneLineHead>
        </SectionHead>
        {loading || !data ? (
          /* FOUR cards, not two: two cohort tables, then two quartile tables, each pair in its
             own grid with a footnote between — the same shape the loaded section has. */
          <SkStack>
            <Grid $cols={2}><LoadingBlock height={340} /><LoadingBlock height={340} /></Grid>
            <SkeletonBox $w="72%" $h="10px" $radius="5px" />
            <Grid $cols={2}><LoadingBlock height={190} /><LoadingBlock height={190} /></Grid>
            <SkeletonBox $w="64%" $h="10px" $radius="5px" />
          </SkStack>
        ) : (
          <>
            <Grid $cols={2}>
              <Card>
                <TitleRow>
                  <CardTitle>Reps whose clients stay</CardTitle>
                  {cohortRange && <RangePill>{cohortRange}</RangePill>}
                </TitleRow>
                {/* [COPY 2026-09-01, user] Title and sub name what the table holds: the
                    columns are clients won and the share still paying at one and two months. */}
                <CardSub>
                  The stronger half of the team: the clients each rep won, and the share still
                  paying one and two months later.
                </CardSub>
                {cohortTop.length ? (
                  <PhoneTableScroll /* [PHONE-LAYOUT 2026-10-22] swipes in its own box on phones; the table alone on a laptop */>
                  <Table>
                    <thead><tr>
                      <Th>Rep</Th><Th $right>Clients won</Th><Th $right>Still at M+1</Th><Th $right>Still at M+2</Th>
                    </tr></thead>
                    <tbody>
                      {cohortTop.map((r) => (
                        <tr key={r.rep}>
                          <Td $strong>{r.rep}</Td>
                          <Td $right>{fmtInt(r.wonClients)}</Td>
                          <Td $right>{fmtPct(r.m1Pct, 0)}</Td>
                          <Td $right><Pill $tone={r.m2Pct >= 75 ? 'good' : r.m2Pct >= 50 ? 'warn' : 'bad'}>{fmtPct(r.m2Pct, 0)}</Pill></Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </PhoneTableScroll>
                ) : <Empty>Not enough cohort data yet.</Empty>}
                {cohortTopLine && <SummaryFoot>{cohortTopLine}</SummaryFoot>}
              </Card>
              <Card>
                <TitleRow>
                  <CardTitle>Reps whose clients drop off</CardTitle>
                  {cohortRange && <RangePill>{cohortRange}</RangePill>}
                </TitleRow>
                <CardSub>
                  The weaker half of the team on the same measure — the further down the list,
                  the faster their clients stop paying.
                </CardSub>
                {cohortBottom.length ? (
                  <PhoneTableScroll /* [PHONE-LAYOUT 2026-10-22] swipes in its own box on phones; the table alone on a laptop */>
                  <Table>
                    <thead><tr>
                      <Th>Rep</Th><Th $right>Clients won</Th><Th $right>Still at M+1</Th><Th $right>Still at M+2</Th>
                    </tr></thead>
                    <tbody>
                      {cohortBottom.map((r) => (
                        <tr key={r.rep}>
                          <Td $strong>{r.rep}</Td>
                          <Td $right>{fmtInt(r.wonClients)}</Td>
                          <Td $right>{fmtPct(r.m1Pct, 0)}</Td>
                          <Td $right><Pill $tone={r.m2Pct >= 75 ? 'good' : r.m2Pct >= 50 ? 'warn' : 'bad'}>{fmtPct(r.m2Pct, 0)}</Pill></Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </PhoneTableScroll>
                ) : <Empty>Not enough cohort data yet.</Empty>}
                {cohortBottomLine && <SummaryFoot>{cohortBottomLine}</SummaryFoot>}
              </Card>
            </Grid>

            <Grid $cols={2} style={{ marginTop: 14 }}>
              <Card>
                <TitleRow>
                  {/* [COPY 2026-09-01, user] "money brought in" everywhere this section speaks;
                      the subs name the table's columns. */}
                  <CardTitle>Top half by money brought in</CardTitle>
                  {scoreboardRange && <RangePill>{scoreboardRange}</RangePill>}
                </TitleRow>
                <CardSub>
                  The stronger half of the team by net money in bank (after refunds and disputes) —
                  with each rep&#39;s sales, conversion and average sale.
                </CardSub>
                <RankTable rows={data.salesReps.topHalf} tone="good" />
                {rankTopLine && <SummaryFoot>{rankTopLine}</SummaryFoot>}
              </Card>
              <Card>
                <TitleRow>
                  <CardTitle>Bottom half by money brought in</CardTitle>
                  {scoreboardRange && <RangePill>{scoreboardRange}</RangePill>}
                </TitleRow>
                <CardSub>
                  The weaker half of the team on the same measure — the further down the list,
                  the less brought in.
                </CardSub>
                <RankTable rows={data.salesReps.bottomHalf} tone="bad" />
                {rankBottomLine && <SummaryFoot>{rankBottomLine}</SummaryFoot>}
              </Card>
            </Grid>
          </>
        )}
      </Section>

      {/* ── 4. retention pipeline ─────────────────────────────────────────── */}
      {/* [PER MONTH 2026-08-17 → OWN PILL 2026-08-31] Holds its OWN month (the pill on the
          title), independent of the headline's and the funnel's.
          ⚠ The RUNNING month is absent by design: mid-month nearly every client still sits in
          Expected, so drawing it would report a near-total loss rather than an unfinished month.
          Selecting it shows a plain note instead. */}
      {(loading || data?.retentionLeak.length) ? (
        <Section $land={retentionLand /* [FLUID-6 2026-10-12] landing + this section's month pick */}>
          <SectionHead>
            <OneLineHead>
              <Eyebrow>Retention pipeline</Eyebrow>
              <TitleSep>|</TitleSep>
              {/* [COPY 2026-09-01, user] Title and note follow the funnel's own steps:
                  Placed inventory → Expected → Retained / Cancelled. */}
              <SectionTitle>How the month&#39;s inventory was kept or lost</SectionTitle>
              <TitleSep>|</TitleSep>
              <OneLineNote>
                The inventory placed with the PMs, what was expected to renew, and how much was
                finally kept or cancelled.
              </OneLineNote>
              {!loading && data && selectedRetentionMonth && (
                <OneLineRight>
                  <PillSelect liquid
                    value={selectedRetentionMonth}
                    deferChange /* [FLUID-8 2026-10-12] instant pick */ onChange={(e) => setRetentionMonth(e.target.value)}
                    title="Pick the month this section shows — independent of the other sections."
                  >
                    {monthOptionNodes}
                  </PillSelect>
                </OneLineRight>
              )}
            </OneLineHead>
          </SectionHead>
          {loading ? <LoadingBlock height={120} /> : (() => {
            const k = data?.retentionLeak.find((x) => x.month === selectedRetentionMonth) ?? null;
            if (!k) {
              return (
                <Card>
                  <Empty>
                    {fmtMonthYear(selectedRetentionMonth)} has not closed yet — until it does, nearly
                    every client still sits in Expected and the split would read as a collapse.
                  </Empty>
                </Card>
              );
            }
            const keptPct = pctOf(k.retained, k.initialExpected);
            return (
              <Card>
                <CardTitle>{fmtMonthYear(k.month)}, step by step</CardTitle>
                <PhoneSwipeFunnelRow>
                  <FunnelStage>
                    <FunnelLabel>Placed inventory</FunnelLabel>
                    <FunnelValue>{fmtMoneyShort(k.somInventory)}</FunnelValue>
                  </FunnelStage>
                  <FunnelGap $tone="good">
                    −{fmtMoneyShort(k.exclusions)}
                    <span>one-time, excluded</span>
                  </FunnelGap>
                  <FunnelStage>
                    <FunnelLabel>Expected</FunnelLabel>
                    <FunnelValue>{fmtMoneyShort(k.initialExpected)}</FunnelValue>
                  </FunnelStage>
                  <FunnelGap $tone={keptPct >= 75 ? 'good' : keptPct >= 50 ? 'warn' : 'bad'}>
                    {fmtPct(keptPct, 0)}
                    <span>retained</span>
                  </FunnelGap>
                  <FunnelStage>
                    <FunnelLabel>Retained</FunnelLabel>
                    <FunnelValue style={{ color: ink.green }}>{fmtMoneyShort(k.retained)}</FunnelValue>
                  </FunnelStage>
                  <FunnelStage>
                    <FunnelLabel>Cancelled</FunnelLabel>
                    <FunnelValue style={{ color: RED }}>{fmtMoneyShort(k.cancelled)}</FunnelValue>
                  </FunnelStage>
                </PhoneSwipeFunnelRow>
                {/* [COPY 2026-08-17] Foot note removed at the user's request. `atRisk` and
                    `pending` are still on the payload if they are wanted back. */}
              </Card>
            );
          })()}
        </Section>
      ) : null}

      {/* ── 5. PMs ────────────────────────────────────────────────────────── */}
      <Section $land={pageLand /* [FLUID-6 2026-10-12] */}>
        <SectionHead>
          {/* [FULL-TIMELINE 2026-08-27, user] This section alone spans ALL data — "make it for
              the complete timeline ... if any pm is from the april 2024 then the data will
              start from there only" — while every other section keeps the 2026 window. Tenure
              months, retention money and the upsell axis all run from `pms.historyFrom`
              (inventory's first month) to now, so the pill states that range, not the window's.
              This section's definition has flipped twice, both times by the user: full history →
              bounded to 2026 (2026-08-17) → full history again (2026-08-27). */}
          {/* [ONE-LINE HEAD 2026-09-01, user] The only section with NO measure line, so it runs
              eyebrow | title and then straight to its range pill — one separator, not two. */}
          <OneLineHead>
            <Eyebrow>Project managers</Eyebrow>
            <TitleSep>|</TitleSep>
            <SectionTitle>Who holds clients longest, and who grows them</SectionTitle>
            {data && (
              <OneLineRight>
                <RangePill>
                  {fmtMonthYear(data.pms.historyFrom ?? data.window.months[0])}
                  {' – '}
                  {fmtMonthYear(data.window.months[data.window.months.length - 1])}
                </RangePill>
              </OneLineRight>
            )}
          </OneLineHead>
        </SectionHead>
        {loading || !data ? (
          /* Left is a 10-row tenure table, right is the quadrant scatter at viewBox 760x330. */
          /* Left is the full-roster tenure table, right is the quadrant scatter at viewBox
             760x480 — keep this ratio in step with QuadrantChart's H. */
          <Grid $cols={2}><LoadingBlock height={560} /><LoadingBlock ratio="760/480" phoneRatio="340/400" /* [PHONE-LAYOUT 2026-10-22] */ /></Grid>
        ) : (
          <Grid $cols={2}>
            <Card>
              {/* [PLAIN HEADINGS 2026-08-27, user] "Longest" read as a client count, and the sub
                  was one long clause. Every column now says its own unit. */}
              <CardTitle>PMs whose clients stay longest</CardTitle>
              {/* [COPY 2026-09-01, user] The sub walks the table's own columns left to right. */}
              <CardSub>
                For each PM: the clients they&#39;ve held, how many months a client stays on
                average, their longest-running client, and how much of the placed money was
                collected.
              </CardSub>
              {data.pms.tenure.length ? (
                <TableScrollX>
                <Table>
                  <thead><tr>
                    <Th>PM</Th><Th $right>Clients</Th><Th $right>Avg months stayed</Th><Th $right>Longest stay (months)</Th><Th $right>Money collected</Th>
                  </tr></thead>
                  <tbody>
                    {/* [ALL PMs 2026-08-17] Was .slice(0, 10). With 17 current PMs that silently
                        dropped the bottom seven — the card is the whole team now. */}
                    {data.pms.tenure.map((r) => (
                      <tr key={r.pm}>
                        <Td $strong>{r.pm}</Td>
                        <Td $right>{fmtInt(r.clients)}</Td>
                        <Td $right><Pill $tone="good">{fmtNum1(r.avgMonths)}</Pill></Td>
                        <Td $right>{fmtInt(r.maxMonths)}</Td>
                        <Td $right>{fmtPct(r.retentionPct, 0)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </TableScrollX>
              ) : <Empty>Not enough data yet.</Empty>}
              {tenureLine && <SummaryFoot>{tenureLine}</SummaryFoot>}
            </Card>
            <Card>
              <CardTitle>Retention against upsell</CardTitle>
              <CardSub>
                Each dot is a PM — how much they keep (retention) against how much they grow
                (upsell). The dashed lines mark the team&#39;s middle, so top-right is strong at
                both.
              </CardSub>
              <QuadrantChart rows={data.pms.quadrant} medianRetention={data.pms.medianRetentionPct}
                medianUpsell={data.pms.medianUpsell} />
              <Legend>
                <LegendItem $color={ink.greenFill} $soft>Strong at both</LegendItem>
                <LegendItem $color={TEAL} $soft>Retention only</LegendItem>
                <LegendItem $color={AMBER} $soft>Upsell only</LegendItem>
                <LegendItem $color={RED} $soft>Below median on both</LegendItem>
              </Legend>
              {quadLine && <SummaryFoot>{quadLine}</SummaryFoot>}
            </Card>
          </Grid>
        )}
      </Section>

      {/* ── 6. momentum ───────────────────────────────────────────────────── */}
      <Section $land={pageLand /* [FLUID-6 2026-10-12] */}>
        <SectionHead>
          {/* [COPY 2026-09-01, user] "month-end close" was jargon; the note now points at what
              each of the two cards actually holds. */}
          <OneLineHead>
            <Eyebrow>Momentum</Eyebrow>
            <TitleSep>|</TitleSep>
            <SectionTitle>How sales and retention are trending</SectionTitle>
            <TitleSep>|</TitleSep>
            <OneLineNote>
              Left: the money new sales brought in each month. Right: the inventory placed with
              the PMs and how much of it they kept.
            </OneLineNote>
          </OneLineHead>
        </SectionHead>
        {loading || !data ? (
          /* Both are ComboCharts at viewBox 760x260. */
          <Grid $cols={2}><LoadingBlock ratio="760/260" /><LoadingBlock ratio="760/260" /></Grid>
        ) : (
          <Grid $cols={2}>
            <Card>
              <CardTitle>New sales by month</CardTitle>
              {/* [REORDER 2026-08-17] Was "the same total as the card above" — true only while
                  this sat directly under the headline. It is now section 4, with Geography and
                  Market in between, so the reference names the card instead of its position. */}
              {/* [COPY 2026-09-01, user] Plain words: name the bar, name the line, say why the
                  line matters. The old sentence referenced the headline card instead of
                  explaining the marks. */}
              <CardSub>
                Each bar is the money new sales brought in that month; the line is the recurring
                part of it — sales that keep paying monthly.
              </CardSub>
              <ComboChart points={salesCombo} barLabel="Total new sales" lineLabel="Recurring"
                formatBar={fmtMoneyShort} formatBarFull={fmtMoney} formatLine={fmtMoney} lineColor={TEAL}
                progress={data.window.inProgress} />
              <Legend>
                <LegendItem $color={ink.barSoft} $soft>Total new sales</LegendItem>
                <LegendItem $color={TEAL}>Recurring portion</LegendItem>
                {mtdLegend && <LegendItem $color={AMBER} $soft>{mtdLegend}</LegendItem>}
              </Legend>
              {salesSummary && (
                <SummaryFoot>{salesSummary[0]}<br />{salesSummary[1]}</SummaryFoot>
              )}
            </Card>
            <Card>
              <CardTitle>Placed inventory and retention</CardTitle>
              <CardSub>
                Placed inventory and In Bank each month, with current retention as a line,
                across every PM.
              </CardSub>
              <ComboChart points={inventoryCombo} barLabel="Placed inventory" lineLabel="Current retention"
                formatBar={fmtMoneyShort} formatBarFull={fmtMoney} formatLine={(n) => fmtPct(n, 2)} lineColor={TEAL}
                progress={data.window.inProgress} extraFirst />
              <Legend>
                <LegendItem $color={ink.barSoft} $soft>Placed inventory</LegendItem>
                <LegendItem $color={TEAL}>Current retention %</LegendItem>
                {mtdLegend && <LegendItem $color={AMBER} $soft>{mtdLegend}</LegendItem>}
              </Legend>
              {inventorySummary && (
                <SummaryFoot>{inventorySummary[0]}<br />{inventorySummary[1]}</SummaryFoot>
              )}
            </Card>
          </Grid>
        )}
      </Section>

      {/* ── 7. client base ────────────────────────────────────────────────── */}
      <Section $land={pageLand /* [FLUID-6 2026-10-12] */}>
        <SectionHead>
          {/* [COPY 2026-09-01, user] Colours live in the section note ONCE; the card sub keeps
              only the reading mechanics (the net figure, the click). */}
          {/* [REDEFINED 2026-08-17] Was "a client counts as active when it carries recurring
              inventory for that month", describing a set-difference on row presence. Both bars
              are now read straight off the Inventory tab so the two agree row for row. */}
          <OneLineHead>
            <Eyebrow>Client base</Eyebrow>
            <TitleSep>|</TitleSep>
            <SectionTitle>New campaigns against cancellations</SectionTitle>
            <TitleSep>|</TitleSep>
            <OneLineNote>
              Green is campaigns that started, red is campaigns cancelled — and the paler red
              slice is clients gone within their second month.
            </OneLineNote>
          </OneLineHead>
        </SectionHead>
        {/* [MASTER-DETAIL 2026-08-27, user] "it takes a lot of space" — this was the page's one
            full-width chart, ~480px tall because rendered height follows card width, with the
            breakdown hidden behind a click. Halved: chart left, breakdown panel right. The
            panel shows the running month by default and follows clicks on the chart. */}
        {loading || !data ? (
          <Grid $cols={2}><LoadingBlock ratio="760/270" /><LoadingBlock ratio="760/270" /></Grid>
        ) : (
          <Grid $cols={2}>
          <Card>
            <CardTitle>Monthly new campaigns and cancellations</CardTitle>
            <CardSub>
              Starts sit above the line, cancellations below, and the figure under each month is
              the net. {isMobile /* [PHONE-LAYOUT 2026-10-22] on a phone the breakdown sits under the chart */
                ? <><strong>Tap a month</strong> to see its breakdown below.</>
                : <><strong>Click a month</strong> to see its breakdown on the right.</>}
            </CardSub>
            <ClientBaseChart points={data.clientBase} progress={data.window.inProgress}
              selected={clientBaseMonth} onPick={setPickedMonth} />
            <Legend>
              <LegendItem $color={ink.greenFill} $soft>New campaigns</LegendItem>
              <LegendItem $color={RED} $soft>Cancelled — already on the book</LegendItem>
              <LegendItem $color={ink.redSoft} $soft>Second Month Cancellation</LegendItem>
              {mtdLegend && <LegendItem $color={AMBER} $soft>{mtdLegend}</LegendItem>}
            </Legend>
            {/* [CLIENT-BASE SUMMARY 2026-09-01, user] The methodology footnote is replaced by
                the computed summary; the running month's thin red bar is covered by the amber
                month-to-date legend entry above. */}
            {clientBaseLine && <SummaryFoot>{clientBaseLine}</SummaryFoot>}
          </Card>
          <DetailCell>
            <DetailCard ref={detailRef /* [FLUID-6 2026-10-12] the breakdown's rows wave on a month click */}>
              {(() => {
                const sel = data.clientBase.find((p) => p.month === clientBaseMonth);
                return sel
                  ? <ClientBaseDetail point={sel} />
                  : <Empty>No data for this period.</Empty>;
              })()}
            </DetailCard>
          </DetailCell>
          </Grid>
        )}
      </Section>

      {/* ── 8. revenue engine ──────────────────────────────────────────────
          [NEW SECTION 2026-08-17 → PLACED 2026-09-01] Added at the end of the page originally,
          "until they have seen it in place"; the user has now placed it — directly after Client
          base, ahead of Geography and Market. If it moves again, move the whole <Section>: the
          two cards answer one question together ("what is a client worth, and which engine
          produced the money") and neither reads as well alone. */}
      {/* [MONEY FLOW 2026-08-27] Was two cards — the revenue mix on the left and refunds on the
          right — which left the reader subtracting one from the other by eye to get what the
          EOM tab calls Net. Clubbed into one full-width card: the same buckets, the same refund
          cells, and the net drawn through them, with the exact figures in a table beneath. The
          user's ask: "club both and show single graph having full data for the EOM month wise".
          Every number here is one that already exists on this tab or on the Payments tab; the
          only arithmetic added is the EOM Net formula, applied server-side in ONE place. */}
      <Section $land={pageLand /* [FLUID-6 2026-10-12] */}>
        <SectionHead>
          {/* [ONE-LINE HEAD 2026-09-01, user] The RangePill takes the same far-right slot the
              month filters do: on this row it answers the same question ("which period?"), and
              leaving it beside the title would put a pill mid-sentence between two separators. */}
          <OneLineHead>
            <Eyebrow>Revenue engine</Eyebrow>
            <TitleSep>|</TitleSep>
            <SectionTitle>Money in, money out, money kept</SectionTitle>
            <TitleSep>|</TitleSep>
            {/* [COPY 2026-08-31, user] "make it in the single row only" — trimmed to the concrete
                half so the note sits on one line. */}
            <OneLineNote>
              Everything collected each month, what went back out, and what was left.
            </OneLineNote>
            {windowLabel && <OneLineRight><RangePill>{windowLabel}</RangePill></OneLineRight>}
          </OneLineHead>
        </SectionHead>
        {/* Full-width chart: the viewBox is 1520x360 — keep this ratio in step with FlowChart's W/H. */}
        {loading || !data ? <LoadingBlock ratio={isMobile ? '760/320' /* [PHONE-LAYOUT 2026-10-22] the phone geometry */ : '1520/360'} /> : (
          <>
          <Card>
            <CardTitle>Gross to net, month by month</CardTitle>
            {/* [COPY 2026-09-01, user] The sub maps every mark on the chart in plain words —
                "the three engines" named as retention, upsells and new sales. */}
            <CardSub>
              Each bar stacks retention, upsells, new sales and PIA to the month&#39;s gross. Red
              below the line is refunds and lost disputes, the small green bar is money won
              back, and the dot is the net we kept — the same figure as the EOM tab&#39;s Net
              SEO Total.
            </CardSub>
            <FlowChart points={data.moneyFlow ?? []} progress={data.window.inProgress} />
            <Legend>
              {/* Reads bottom-up, matching the stack itself. */}
              <LegendItem $color={ink.mix.retention} $soft>Retention</LegendItem>
              <LegendItem $color={ink.mix.upsell} $soft>Upsell / SO</LegendItem>
              <LegendItem $color={ink.mix.newSales} $soft>New sales</LegendItem>
              <LegendItem $color={ink.mix.pia} $soft>PIA</LegendItem>
              <LegendItem $color={ink.mix.shopify} $soft>Shopify</LegendItem>
              <LegendItem $color={ink.outflow} $soft>Refunds and lost disputes</LegendItem>
              <LegendItem $color={ink.greenFill} $soft>Disputes won / recovery</LegendItem>
              <LegendItem $color={ink.strong}>Net kept</LegendItem>
              {mtdLegend && <LegendItem $color={AMBER} $soft>{mtdLegend}</LegendItem>}
            </Legend>
            {/* [MONEY-FLOW SUMMARY 2026-09-01, user] The reconciliation footnote is replaced by
                the computed summary. The reconciliation FACTS still hold, just unstated: the four
                buckets share no payment column (nothing counted twice), and net follows EOMPage's
                own formula. [PIA IN GROSS 2026-09-01] Net now MATCHES EOM's Net SEO Total month
                for month — PIA was the gap and is the fourth bucket above. The two remaining
                pennies-apart months are EOM dropping untagged refund/dispute rows, not money
                missing here; see processAnalysis.ts's MoneyFlowPoint docblock. */}
            {moneyFlowSummary && (
              <SummaryFoot>{moneyFlowSummary[0]}<br />{moneyFlowSummary[1]}</SummaryFoot>
            )}
          </Card>

          {/* [MONEY COMPARE 2026-08-31, user] "on the left it will show the year comparison, on
              the right quarter comparison, and below that in the middle the month thing." Every
              figure below is a plain sum of the server's monthly compare series, whose 2026
              points are the moneyFlow series itself — so the chart above and these cards can
              never disagree about a 2026 month. */}
          {cmpView && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <SplitRow>
                <Card>
                  <CardTitle>Year against year</CardTitle>
                  {/* [COPY 2026-09-01, user] Subs reworked in the plain-English pass; each
                      compare card now closes with its computed summary line. */}
                  <CardSub>
                    One bar per year, palest is oldest — gross on the bar, the dot is the money
                    kept. 2024&#39;s data starts in April.
                  </CardSub>
                  <FlowGroups viewW={isMobile ? 360 : 460 /* [PHONE-LAYOUT 2026-10-22] three bars need no more on a phone */} spread
                    groups={[{ label: '', points: cmpView.years }]}
                    barLabelOf={(m) => cmpView.spanByKey.get(m) ?? m}
                    titleOf={(m) => cmpView.spanByKey.get(m) ?? m}
                    partialKey={cmpView.running ? '2026' : null}
                    partialLabel={cmpView.yPartialLabel} />
                  {cmpView.yearSummary && <SummaryFoot>{cmpView.yearSummary}</SummaryFoot>}
                </Card>
                <Card>
                  <CardHeadRow>
                    <CardTitle>Quarter against quarter</CardTitle>
                    <CardHeadNote>
                      ▲▼ beside a year is how its money kept compares with the year before.
                    </CardHeadNote>
                  </CardHeadRow>
                  <CardSub>
                    Each quarter side by side across the years — {isMobile ? 'tap' /* [PHONE-LAYOUT 2026-10-22] */ : 'hover'} a bar for its full
                    breakdown.
                  </CardSub>
                  {/* [YEAR-ON-YEAR 2026-09-01, user] "remove the up down arrow from the quarter
                      name and use it to the year name on the x axis, so that i can see the year
                      on year change" — the bars are already grouped BY quarter, so the figure on
                      the quarter label restated the grouping; on the years it answers the
                      question the chart is actually for. */}
                  <FlowGroups viewW={isMobile ? 820 : 1060 /* [PHONE-LAYOUT 2026-10-22] the bars at full size without the wide card's empty side margins */} deltaOn="bar"
                    groups={cmpView.quarterGroups}
                    partialKey={cmpView.qPartialKey}
                    partialLabel="still filling in" />
                  {cmpView.quarterSummary && <SummaryFoot>{cmpView.quarterSummary}</SummaryFoot>}
                </Card>
              </SplitRow>
              <Card>
                <CardHeadRow>
                  <CardTitle>Month against month</CardTitle>
                  <CardHeadNote>
                    ▲▼ above a year is how its money kept compares with the year before.
                  </CardHeadNote>
                </CardHeadRow>
                <CardSub>
                  Each month side by side across the years — the month still running is dashed.
                </CardSub>
                {/* [YEAR-ON-YEAR 2026-09-01, user] Same move as the quarter card — the figure
                    belongs on the year, not on the month name the grouping already states. This
                    chart is dense, so it STACKS the figure under the year (see stackDelta). */}
                <FlowGroups viewW={isMobile ? 1200 : 1520 /* [PHONE-LAYOUT 2026-10-22] slimmer bars (labels still clear) so the phone swipe is shorter */} deltaOn="bar"
                  groups={cmpView.monthGroups}
                  partialKey={cmpView.mPartialKey}
                  partialLabel={data.window.inProgress
                    ? `day ${data.window.inProgress.day} of ${data.window.inProgress.daysInMonth}` : undefined} />
                {cmpView.monthSummary && <SummaryFoot>{cmpView.monthSummary}</SummaryFoot>}
              </Card>
            </div>
          )}
          </>
        )}
      </Section>
      {/* ── 9. geography ──────────────────────────────────────────────────── */}
      <Section $land={pageLand /* [FLUID-6 2026-10-12] */}>
        <SectionHead>
          {/* [SECTION RANGE 2026-08-17] The window was only stated once, up in the page header,
              so this section read as undated. It covers the whole tab window — the payments,
              refunds and disputes behind it are all bounded by window.start..window.end. */}
          {/* [PILLS 2026-08-17] Restored after a brief removal — the user prefers each section to
              state its own window rather than relying on the page header alone. */}
          {/* [COPY 2026-09-01, user] Plain-English pass: the title names the ranking, the note
              walks a first-time reader through exactly what one card contains. */}
          <OneLineHead>
            <Eyebrow>Geography</Eyebrow>
            <TitleSep>|</TitleSep>
            <SectionTitle>Our top five states by revenue</SectionTitle>
            <TitleSep>|</TitleSep>
            <OneLineNote>
              Each card is one state — how many clients it has, the money kept from them, and the
              industries that money comes from.
            </OneLineNote>
            {windowLabel && <OneLineRight><RangePill>{windowLabel}</RangePill></OneLineRight>}
          </OneLineHead>
          {/* [COVERAGE 2026-08-17] State is known for barely a quarter of revenue, and until this
              line existed the five cards below read as the whole business. */}
        </SectionHead>
        {loading || !data ? (
          /* FIVE, not three — TOP_N_STATES is 5, and a 3-of-5 grid left two columns empty.
             Mirrors the real grid's isMobile switch. State cards have no title/caption. */
          <Grid $cols={isMobile ? 1 : 5}>
            {[0, 1, 2, 3, 4].map((i) => <LoadingBlock key={i} height={158} head={false} />)}
          </Grid>
        ) : (
          <>
            <Grid $cols={isMobile ? 1 : 5}>
              {data.geography.states.map((s: PaStateRow) => (
                <StateCard key={s.state}>
                  <StateHead>
                    <div>
                      <StateName>{s.state}</StateName>
                      <BarMeta>{fmtInt(s.clients)} clients</BarMeta>
                    </div>
                    <StateTotal>{fmtMoneyShort(s.inBank)}</StateTotal>
                  </StateHead>
                  {s.industries.map((i) => (
                    <StateRowLine key={i.industry}>
                      {/* [2026-08-27, user] No title attr: the themed capsule was popping over
                          every row ("no need of tooltip here"). Long names just ellipsize. */}
                      <StateInd>{i.industry}</StateInd>
                      <StateVal>{fmtMoneyShort(i.inBank)}</StateVal>
                    </StateRowLine>
                  ))}
                </StateCard>
              ))}
            </Grid>
          </>
        )}
      </Section>

      {/* ── 10. market ─────────────────────────────────────────────────────── */}
      <Section $land={pageLand /* [FLUID-6 2026-10-12] */}>
        <SectionHead>
          {/* Both cards cover the whole tab window: revenue comes from the same window-bounded
              payments/refunds/disputes as Geography, and the conversion query is bounded by the
              same window.start..window.end. */}
          <OneLineHead>
            <Eyebrow>Market</Eyebrow>
            <TitleSep>|</TitleSep>
            <SectionTitle>Which industries pay us, and which convert</SectionTitle>
            <TitleSep>|</TitleSep>
            <OneLineNote>
              Revenue is what we kept: payments received, subtract refunds and lost disputes.
            </OneLineNote>
            {windowLabel && <OneLineRight><RangePill>{windowLabel}</RangePill></OneLineRight>}
          </OneLineHead>
        </SectionHead>
        {loading || !data ? (
          /* Ten industry rows at ~30px each. */
          <Grid $cols={2}><LoadingBlock height={300} /><LoadingBlock height={300} /></Grid>
        ) : (
          <Grid $cols={2}>
            <Card>
              <CardTitle>Top industries by revenue</CardTitle>
              {/* Year dropped from the copy — the range pill on the section heading carries it,
                  and a hardcoded "2026" would go stale on its own. */}
              <CardSub>Total revenue kept, by the client's industry. This is the size of each market to us.</CardSub>
              <BarList items={industryBars(data.industry.byInBank, 'inbank')} color={ink.greenFill} />
            </Card>
            <Card>
              <CardTitle>Top industries by conversion</CardTitle>
              <CardSub>Of the leads we worked in each industry, the share that became a deal. This is where our pitch lands best.</CardSub>
              <BarList
                items={data.industry.byConversion.map((r) => ({
                  name: r.industry,
                  value: r.conversionPct,
                  primary: fmtPct(r.conversionPct, 2),
                  meta: `${fmtInt(r.sales)} of ${fmtInt(r.leads)} leads`,
                }))}
                color={TEAL} />
            </Card>
          </Grid>
        )}
      </Section>


    </Container>
  );
};

/** Shared by the two money-booked cards — same columns, same order, one half each. */
const RankTable: React.FC<{ rows: { rep: string; inBank: number; sales: number; conversionPct: number; atv: number }[]; tone: 'good' | 'bad' }> = ({ rows, tone }) => {
  if (!rows.length) return <Empty>Roster too small to split.</Empty>;
  return (
    <PhoneTableScroll /* [PHONE-LAYOUT 2026-10-22] swipes in its own box on phones; the table alone on a laptop */>
    <Table>
      <thead><tr>
        {/* [PA-RANKING 2026-09-17, user] Net In-Bank: deals − the rep's refunds/disputes + disputes
            won, same figure and same people as the Sales Scoreboard; ATV follows it. */}
        <Th>Rep</Th><Th $right>Net In-Bank</Th><Th $right>Sales</Th><Th $right>Conv %</Th><Th $right>ATV</Th>
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.rep}>
            <Td $strong>{r.rep}</Td>
            <Td $right><Pill $tone={tone}>{fmtMoneyShort(r.inBank)}</Pill></Td>
            <Td $right>{fmtInt(r.sales)}</Td>
            <Td $right>{fmtPct(r.conversionPct, 1)}</Td>
            <Td $right>{fmtMoneyShort(r.atv)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
    </PhoneTableScroll>
  );
};

export default ProcessAnalysisPage;
